"use client"

import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode, useRef } from "react"
import type { 
  WorkflowItem,
  WorkflowStage,
  Filters, 
  WorkflowState, 
  WorkflowAction,
  User,
  WorkflowConfiguration,
  StageConfiguration,
  FieldConfiguration,
  AssignedUser,
  ClientCustomField
} from "@/types/workflow"
import type { SavedView, UserViewsResponse } from "@/types/saved-view"
import { LoadingState, FboEntityType } from "@/types/workflow"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useEntityType } from "@/contexts/entity-type-context"
import { useAuth } from "@/contexts/auth-context"
import { useToast } from "@/hooks/use-toast"
import { useCodeValues } from "@/hooks/use-code-values"
import { ensureCodeValuesLoaded } from "@/lib/code-values-loader"
import { getFacetKeysFromConfiguration, getSelectFieldsFromConfiguration } from "@/utils/column-type-helpers"
import { readDeepLinkParams } from "@/lib/deep-link-params"
import {
  isRequestsGroupEntity,
  useRequestsGroupState,
  type RequestsGroupEntityType,
} from "@/contexts/requests-group-state-context"

// Simple debounce function
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T {
  let timeoutId: NodeJS.Timeout
  return ((...args: any[]) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }) as T
}

// Initial state
const initialState: WorkflowState = {
  // UI State
  currentStage: undefined,
  isFilterDrawerOpen: false,
  isBulkEditModalOpen: false,
  currentBulkEditField: null,
  
  // Data State
  workflowItems: [],
  pendingOptimistic: {},
  pendingRemovals: {},
  columns: [],
  visibleColumns: [],
  filters: {},
  selectedItems: [],
  
  // Pagination State
  currentPage: 1,
  pageSize: 5,
  itemsPerPage: null, // null = auto, number = fixed page size
  totalCount: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  
  // Loading State
  loadingState: LoadingState.IDLE,
  
  // Error State
  error: null,
  
  // Views State
  savedViews: [],
  currentView: null,
  hasUnsavedChanges: false,
  
  // View data from API
  viewsResponse: null,
  
  // User State
  currentUser: {
    id: "1",
    name: "John Doe",
    count: 0,
    role: "Administrator",
    email: "john.doe@company.com"
  },

  // Configuration State
  configuration: undefined,
  isLoadingConfiguration: false,
  configurationError: null,

  // Stages
  stages: [],

  // Assigned Users State
  assignedUsers: [],
  isLoadingAssignedUsers: false,
  assignedUsersError: null,

  // Facets
  facets: undefined,
  stageCounts: undefined,

  // New state for stageFilters and globalFilters
  stageFilters: [],
  globalFilters: [],
  temporaryQueryFilter: null,
  temporaryQueryFilterLabel: null,
  temporaryQueryFilterPausesStageFilters: false,

  // New state for work orders facets
  workOrdersFacets: undefined,

  // Code Values State
  codeValuesData: undefined,
  isLoadingCodeValues: false,
  codeValuesError: null,

  // Column Widths State
  columnWidths: {},
  messageNotificationsEnabled: true,
  // Pinned columns per stage
  pinnedColumnsByStage: {},
  sortByStage: {},

  // Show-completed toggle state per stage (for fixed-filter stages)
  showCompletedByStage: {} as Record<string, boolean>,

  // Client Custom Fields State
  originalConfiguration: undefined,
  activeClientFboId: null,
  activeClientName: null,
  cachedClientCustomFields: new Map(),
  customFieldsApplied: false,
  isHandlingClientFilterChange: false,
}

/**
 * The stage to land on: a `?tab=` deep link naming a stage code beats the
 * user's last stage (localStorage `wfh_active_stage`), which beats the
 * configuration's first stage. Each candidate is validated against the stages
 * we actually have, so a stale or bogus value just falls through.
 *
 * Only the root WorkOrder provider honours the deep link. A `?tab=` value is
 * either a WorkOrder stage code or a Requests-group tab id (WorkflowHub owns
 * that half); letting a per-tab provider match it against ITS stages could
 * coincidentally select a same-named stage on a tab the link never named.
 *
 * Safe to call from effects/callbacks only — readDeepLinkParams rewrites the
 * URL on its first call.
 *
 * Not guarded by `isDeepLinkApplied()`, which covers only the tab half: effects
 * run child-before-parent, so WorkflowHub's effect has already marked the link
 * applied by the time this runs on a first load, and guarding here would drop
 * `?tab=<stage code>` on every visit. A remount of the provider could therefore
 * re-select the deep-linked stage from the still-cached params — reachable only
 * if this microapp is ever hosted somewhere that remounts it without a document
 * load, which the iframe never does. It runs only when no stage is set yet, so
 * `isStageChange` is false and no filters or selection are cleared.
 */
function resolveInitialStage(
  stages: { stageCode: string }[],
  entityType: string | null | undefined,
): WorkflowStage {
  const candidates: (string | null)[] = []
  if (entityType === 'WorkOrder') candidates.push(readDeepLinkParams().tab)
  try {
    candidates.push(typeof window !== 'undefined' ? localStorage.getItem('wfh_active_stage') : null)
  } catch {
    // ignore storage errors
  }
  // Matched case-insensitively, like every other stageCode comparison in this
  // microapp — `?tab=` is host-supplied, so `active` has to find `Active`.
  // Returns the CANONICAL stageCode, never the caller's casing: <TabsContent>
  // matches `effectiveTab === stage.stageCode` exactly, so handing back
  // 'active' would select a tab that renders nothing at all.
  for (const candidate of candidates) {
    if (!candidate) continue
    const match = stages.find(s => s.stageCode.toLowerCase() === candidate.toLowerCase())
    if (match) return match.stageCode as WorkflowStage
  }
  return stages[0].stageCode as WorkflowStage
}

// Reducer function
function workflowReducer(state: WorkflowState, action: WorkflowAction): WorkflowState {
  switch (action.type) {
    case 'SET_CURRENT_STAGE': {
      // Clearing the temporary filter is only correct for a REAL stage
      // change. Two non-changes must preserve it:
      //   - initial bootstrap (undefined -> first stage): the cross-tab
      //     search handoff applies the filter in the same React commit as
      //     this bootstrap dispatch, so the two get batched and the "filter
      //     applied" state never renders — the grid's re-apply effect sees
      //     temporaryQueryFilter as null -> null (no dep change) and never
      //     fires again, silently losing the handoff.
      //   - re-dispatch of the CURRENT stage (clicking the already-active
      //     tab): nothing navigated, so the "Showing" pill must survive.
      const isStageChange = state.currentStage != null && state.currentStage !== action.payload;
      return {
        ...state,
        currentStage: action.payload,
        currentPage: 1, // Reset to first page when changing stage
        selectedItems: [], // Clear selections when changing stage
        filters: {}, // Clear filters when changing stage
        temporaryQueryFilter: isStageChange ? null : state.temporaryQueryFilter,
        temporaryQueryFilterLabel: isStageChange ? null : state.temporaryQueryFilterLabel,
        temporaryQueryFilterPausesStageFilters: isStageChange ? false : state.temporaryQueryFilterPausesStageFilters,
        loadingState: LoadingState.LOADING_WORK_ORDERS // Set loading state immediately
      }
    }
    
    case 'SET_LOADING_STATE':
      return { ...state, loadingState: action.payload }
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loadingState: LoadingState.IDLE }
    
    case 'TOGGLE_FILTER_DRAWER':
      return { ...state, isFilterDrawerOpen: !state.isFilterDrawerOpen }
    
    case 'SET_WORK_ORDERS': {
      // Force a new array reference to ensure React detects the change
      // Removal overlay: drop rows the user optimistically deleted from fetched
      // results while the removal is still within its suppression window —
      // REMOVE_WORK_ORDER only filters the in-memory array, so a silent refetch
      // racing Azure Search index propagation would otherwise resurrect the row
      // (vanish → reappear → vanish again at the 20s safety refetch). The window
      // outlives that 20s safety refetch so its response can't resurrect either;
      // removals are only registered after the delete API succeeded, so if the
      // index truly never catches up the row legitimately returns on the next
      // natural fetch after expiry.
      const REMOVAL_TIMEOUT_MS = 25000;
      const nowForRemovals = Date.now();
      const priorRemovals = state.pendingRemovals || {};
      const nextRemovals: WorkflowState['pendingRemovals'] = {};
      for (const [removedId, removedTs] of Object.entries(priorRemovals)) {
        if (nowForRemovals - removedTs <= REMOVAL_TIMEOUT_MS) nextRemovals[removedId] = removedTs;
      }
      const rawFetchedItems = [...(action.payload.data || [])];
      const fetchedItems = rawFetchedItems.filter(item => !(item.id != null && item.id in nextRemovals));
      const suppressedCount = rawFetchedItems.length - fetchedItems.length;
      // Optimistic overlay: re-apply any still-pending optimistic values on top of the
      // freshly-fetched rows. A pending field clears when the server confirms it (fetched
      // value matches the expected value) or when it has been pending longer than the
      // safety timeout (then we trust the server). This stops optimistic edits from
      // flickering back while the search index is still catching up.
      const OPTIMISTIC_TIMEOUT_MS = 20000;
      const now = Date.now();
      const pending = state.pendingOptimistic || {};
      const hasPending = Object.keys(pending).length > 0;
      const nextPending: WorkflowState['pendingOptimistic'] = {};
      const mergedItems = fetchedItems.map(item => {
        const rowPending = pending[item.id];
        if (!rowPending) return item;
        let next = item;
        const keepFields: Record<string, { expected: any; prior: any; ts: number }> = {};
        for (const [field, info] of Object.entries(rowPending)) {
          const confirmed = JSON.stringify((item as any)[field]) === JSON.stringify(info.expected);
          const expired = now - info.ts > OPTIMISTIC_TIMEOUT_MS;
          if (confirmed || expired) continue; // server caught up (or timed out) -> drop overlay
          if (next === item) next = { ...item };
          (next as any)[field] = info.expected; // still pending -> keep showing optimistic value
          keepFields[field] = info;
        }
        if (Object.keys(keepFields).length > 0) nextPending[item.id] = keepFields;
        return next;
      });
      // Keep pending entries for rows not in this fetch (other page / temporarily absent),
      // unless they've exceeded the timeout.
      if (hasPending) {
        const presentIds = new Set(fetchedItems.map(i => i.id));
        for (const [id, fields] of Object.entries(pending)) {
          if (presentIds.has(id)) continue;
          const keep: Record<string, { expected: any; prior: any; ts: number }> = {};
          for (const [field, info] of Object.entries(fields)) {
            if (now - info.ts <= OPTIMISTIC_TIMEOUT_MS) keep[field] = info;
          }
          if (Object.keys(keep).length > 0) nextPending[id] = keep;
        }
      }
      return {
        ...state,
        workflowItems: mergedItems,
        pendingOptimistic: nextPending,
        pendingRemovals: nextRemovals,
        // Keep the count consistent with the rows we actually render: a
        // suppressed (pending-removal) row is still counted by the lagging
        // index, so subtract it like REMOVE_WORK_ORDER did.
        totalCount: Math.max(0, (action.payload.totalCount ?? 0) - suppressedCount),
        hasNextPage: action.payload.hasNextPage,
        hasPreviousPage: action.payload.hasPreviousPage,
        loadingState: LoadingState.IDLE,
        error: null,
        workOrdersFacets: action.payload.facets ?? undefined
      }
    }
    
    case 'SET_COLUMNS':
      return { 
        ...state, 
        columns: action.payload.columns,
        visibleColumns: action.payload.visibleColumns,
        // Don't reset loading state here - keep it until data is loaded
      }
    
    case 'UPDATE_VISIBLE_COLUMNS':
      return { 
        ...state, 
        visibleColumns: action.payload,
        hasUnsavedChanges: true
      }
    
    case 'UPDATE_FILTER':
      return { 
        ...state, 
        filters: { ...state.filters, [action.payload.key]: action.payload.value },
        currentPage: 1, // Reset to first page when filtering
        hasUnsavedChanges: true
      }

    
    case 'SET_SELECTED_ITEMS':
      return { ...state, selectedItems: action.payload }
    
    case 'TOGGLE_ITEM_SELECTION':
      const isSelected = state.selectedItems.includes(action.payload)
      return {
        ...state,
        selectedItems: isSelected
          ? state.selectedItems.filter(id => id !== action.payload)
          : [...state.selectedItems, action.payload]
      }
    
    case 'SELECT_ALL_ITEMS':
      return { ...state, selectedItems: action.payload }
    
    case 'DESELECT_ALL_ITEMS':
      return { ...state, selectedItems: [] }
    
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload }
    
    case 'SET_PAGE_SIZE':
      return { 
        ...state, 
        pageSize: action.payload,
        currentPage: 1 // Reset to first page when changing page size
      }
    
    case 'SET_ITEMS_PER_PAGE':
      return { 
        ...state, 
        itemsPerPage: action.payload,
        currentPage: 1 // Reset to first page when changing items per page
      }
    
    case 'UPDATE_WORK_ORDER_FIELD':
      const updatedItems = state.workflowItems.map(item =>
        item.id === action.payload.orderId
          ? { ...item, [action.payload.field]: action.payload.value }
          : item
      );
      
      return {
        ...state,
        workflowItems: updatedItems
      }
    
    case 'APPLY_OPTIMISTIC_UPDATES': {
      const ts = Date.now();
      const nextPending: WorkflowState['pendingOptimistic'] = { ...(state.pendingOptimistic || {}) };
      const updatedItemsOptimistic = state.workflowItems.map(item => {
        const update = action.payload.updates.find(u => u.id === item.id);
        if (!update) return item;
        // Record each field as pending: keep the original prior value (so a revert goes all
        // the way back to server-truth even across repeated edits) and mark it pending so
        // SET_WORK_ORDERS won't clobber it before the server confirms.
        const rowPending = { ...(nextPending[item.id] || {}) };
        for (const [field, value] of Object.entries(update.updates)) {
          const prior = rowPending[field] ? rowPending[field].prior : (item as any)[field];
          rowPending[field] = { expected: value, prior, ts };
        }
        nextPending[item.id] = rowPending;
        return { ...item, ...update.updates };
      });
      return {
        ...state,
        workflowItems: updatedItemsOptimistic,
        pendingOptimistic: nextPending
      }
    }

    case 'REVERT_OPTIMISTIC': {
      // API failed -> restore the prior value(s) for the given fields and drop them from pending.
      const { id, fields } = action.payload;
      const rowPending = state.pendingOptimistic?.[id];
      if (!rowPending) return state;
      const restoredItems = state.workflowItems.map(item => {
        if (item.id !== id) return item;
        const next: any = { ...item };
        for (const f of fields) {
          if (rowPending[f]) next[f] = rowPending[f].prior;
        }
        return next;
      });
      const nextPending: WorkflowState['pendingOptimistic'] = { ...state.pendingOptimistic };
      const remaining = { ...rowPending };
      for (const f of fields) delete remaining[f];
      if (Object.keys(remaining).length > 0) nextPending[id] = remaining; else delete nextPending[id];
      return { ...state, workflowItems: restoredItems, pendingOptimistic: nextPending };
    }
    
    case 'REMOVE_WORK_ORDER':
      return {
        ...state,
        workflowItems: state.workflowItems.filter(item => item.id !== action.payload),
        // Register the id so SET_WORK_ORDERS keeps suppressing it while the
        // search index catches up (see the removal overlay there).
        pendingRemovals: { ...(state.pendingRemovals || {}), [action.payload]: Date.now() },
        totalCount: Math.max(0, state.totalCount - 1),
        hasNextPage: state.hasNextPage && state.workflowItems.length > state.pageSize
      }
    
    case 'OPEN_BULK_EDIT_MODAL':
      return { 
        ...state, 
        isBulkEditModalOpen: true,
        currentBulkEditField: action.payload
      }
    
    case 'CLOSE_BULK_EDIT_MODAL':
      return { 
        ...state, 
        isBulkEditModalOpen: false,
        currentBulkEditField: null
      }
    
    case 'SET_CURRENT_VIEW':
      return { 
        ...state, 
        currentView: action.payload,
        currentPage: 1, // Reset to first page when changing view
        selectedItems: [] // Clear selections when changing view
      }
    
    case 'SET_SAVED_VIEWS':
      return { ...state, savedViews: action.payload }
    
    case 'SET_UNSAVED_CHANGES':
      return { ...state, hasUnsavedChanges: action.payload }
    
    case 'ADD_CUSTOM_COLUMN':
      return { 
        ...state, 
        columns: [...state.columns, action.payload],
        visibleColumns: [...state.visibleColumns, action.payload]
      }
    
    case 'DELETE_COLUMN':
      return { 
        ...state, 
        columns: state.columns.filter(col => col.fieldConfigurationId !== action.payload),
        visibleColumns: state.visibleColumns.filter(col => col.fieldConfigurationId !== action.payload)
      }
    
    case 'SET_CONFIGURATION': {
      // Find the saved view that matches the viewSettingsId from configuration
      // Search all view sources: defaultView, userViews, sharedViews, and publicViews
      let matchingView: SavedView | null = null;
      
      if (action.payload.viewSettingsId && state.viewsResponse) {
        // Check default view first
        if (state.viewsResponse.defaultView?.viewSettingsId === action.payload.viewSettingsId) {
          matchingView = state.viewsResponse.defaultView;
        }
        // Check user views
        if (!matchingView) {
          matchingView = state.viewsResponse.userViews.find(v => v.viewSettingsId === action.payload.viewSettingsId) || null;
        }
        // Check shared views
        if (!matchingView) {
          matchingView = state.viewsResponse.sharedViews.find(v => v.viewSettingsId === action.payload.viewSettingsId) || null;
        }
        // Check public views
        if (!matchingView) {
          matchingView = state.viewsResponse.publicViews.find(v => v.viewSettingsId === action.payload.viewSettingsId) || null;
        }
        // Fallback to savedViews if still not found
        if (!matchingView) {
          matchingView = state.savedViews.find(view => view.viewSettingsId === action.payload.viewSettingsId) || null;
        }
      }

      // Build pinned columns map from configuration's stage configurations
      const pinnedFromConfig = (() => {
        try {
          const map: Record<string, string | null> = {}
          for (const sc of (action.payload.stageConfigurations || [])) {
            if (sc && sc.stageId) {
              map[sc.stageId] = (sc as any).pinnedColumnId ?? null
            }
          }
          return map
        } catch {
          return {}
        }
      })()

      // Seed showCompleted toggle per stage from the saved view state returned
      // by the API. The backend persists this on ViewStageConfigurationDto and
      // GET /configurations merges it onto TenantStageConfigurationDto with the
      // same UserViewState → ViewSettings precedence as PinnedColumnId / Sort.
      const showCompletedFromConfig = (() => {
        try {
          const map: Record<string, boolean> = {}
          for (const sc of (action.payload.stageConfigurations || [])) {
            if (sc && sc.stageId) {
              map[sc.stageId] = (sc as any).showCompleted ?? false
            }
          }
          return map
        } catch {
          return {}
        }
      })()

      // Restore saved sort state per stage from configuration
      // The sortColumn may be either a columnCode (correct) or a fieldConfigurationId (legacy).
      // Resolve fieldConfigurationId → columnCode so the grid can match it to actual columns.
      const sortFromConfig = (() => {
        try {
          const map: Record<string, { columnCode: string; direction: 'asc' | 'desc' } | null> = {}
          for (const sc of (action.payload.stageConfigurations || [])) {
            if (sc && sc.stageId) {
              let col = (sc as any).sortColumn
              const dir = (sc as any).sortDirection
              if (col && dir) {
                // Check if col is a fieldConfigurationId rather than a columnCode
                // by looking it up in the stage's field configurations
                const matchedField = (sc.fieldConfigurations || []).find(
                  (f: any) => f.fieldConfigurationId === col
                )
                if (matchedField) {
                  col = matchedField.columnCode // resolve to actual column code
                }
                map[sc.stageId] = { columnCode: col, direction: dir as 'asc' | 'desc' }
              } else {
                map[sc.stageId] = null
              }
            }
          }
          return map
        } catch {
          return {}
        }
      })()

      let newConfiguration = action.payload

      // If custom fields were previously applied, re-apply them to the new configuration
      if (state.customFieldsApplied && state.activeClientFboId && state.cachedClientCustomFields.has(state.activeClientFboId)) {
        const cachedFields = state.cachedClientCustomFields.get(state.activeClientFboId)
        if (cachedFields) {
          // Helper function to apply custom fields to field configurations
          const applyCustomFieldsToFields = (fields: FieldConfiguration[]): FieldConfiguration[] => {
            return fields.map(field => {
              if (field.mappingSource === 'FboCustomField' && field.mappingSourceTargetColumn) {
                const customField = cachedFields.find(cf => cf.FieldName === field.mappingSourceTargetColumn)
                if (customField) {
                  return {
                    ...field,
                    columnNameEnglish: customField.DisplayName,
                    columnNameFrench: customField.DisplayName,
                    // Explicitly set customValues - either parsed array or empty array to clear stale values
                    customValues: customField.SelectValues 
                      ? customField.SelectValues.split(',').map(val => ({
                          code: val.trim(),
                          titleEnglish: val.trim(),
                          titleFrench: val.trim(),
                          color: '',
                          isDeleted: false
                        }))
                      : []
                  }
                }
              }
              return field
            })
          }
          
          newConfiguration = {
            ...newConfiguration,
            fieldsInUse: applyCustomFieldsToFields(newConfiguration.fieldsInUse),
            stageConfigurations: newConfiguration.stageConfigurations.map(stage => ({
              ...stage,
              fieldConfigurations: applyCustomFieldsToFields(stage.fieldConfigurations)
            }))
          }
        }
      }
      
      // "Soft refresh" path (column reorder, jump-to-column, etc.): keep
      // the user's in-flight Show-Completed toggle and pinned-column choice
      // intact. Without this, every config refresh would reset those maps
      // from the server's last-saved snapshot — which is stale by the time
      // the user has clicked anything that triggers a re-fetch. View
      // switches dispatch SET_CURRENT_VIEW and then SET_CONFIGURATION
      // WITHOUT this flag, so the new view's saved prefs do apply.
      const preserveClientPrefs = (action.payload as any)._preserveClientPrefs === true
      return {
        ...state,
        configuration: newConfiguration,
        stages: newConfiguration.stageConfigurations || [],
        pinnedColumnsByStage: preserveClientPrefs ? state.pinnedColumnsByStage : pinnedFromConfig,
        showCompletedByStage: preserveClientPrefs ? state.showCompletedByStage : showCompletedFromConfig,
        sortByStage: sortFromConfig,
        isLoadingConfiguration: false,
        configurationError: null,
        // Store original configuration only if not already stored or if custom fields are not applied
        originalConfiguration: state.originalConfiguration || action.payload,
        // Apply saved filters and view settings if present
        ...(action.payload.savedGlobalFilters && {
          globalFilters: action.payload.savedGlobalFilters.map(filter => ({
            fieldConfigurationId: filter.fieldConfigurationId,
            filter: filter.filter,
            values: filter.values // Keep as string, don't parse to object
          }))
        }),
        ...(action.payload.savedStageFilters && {
          stageFilters: action.payload.savedStageFilters.map(filter => ({
            stageId: filter.stageId,
            fieldConfigurationId: filter.fieldConfigurationId,
            filter: filter.filter,
            values: filter.values ? JSON.parse(filter.values) : undefined
          }))
        }),
        // Notifications preference: nullable end to end, and the read side mirrors the save contract —
        // omitted means "leave alone", NOT "reset to on". This preference is user-global (not per view),
        // and most SET_CONFIGURATION payloads (soft refresh, view switch, a save of unrelated grid state)
        // do not echo it, so falling straight back to `true` re-armed mark-read-on-open on every such
        // refresh and silently marked a client escalation read after the user had turned notifications
        // off. Preserve the live value when the payload omits it; default to on only when it has never
        // been set (initial state is already `true`, so that final fallback is belt-and-suspenders).
        messageNotificationsEnabled: action.payload.messageNotificationsEnabled ?? state.messageNotificationsEnabled ?? true,
        // Apply saved column widths if present (deserialize from JSON string)
        ...(action.payload.columnWidths && {
          columnWidths: (() => {
            try {
              return JSON.parse(action.payload.columnWidths)
            } catch (error) {
              console.warn('Failed to deserialize columnWidths:', error)
              return {}
            }
          })()
        }),
        // Set current view based on viewSettingsId if present and different from current
        ...(action.payload.viewSettingsId && (!state.currentView || state.currentView.viewSettingsId !== action.payload.viewSettingsId) && {
          currentView: matchingView || null
        })
      }
    }
    
    case 'SET_LOADING_CONFIGURATION':
      return { ...state, isLoadingConfiguration: action.payload }
    
    case 'SET_CONFIGURATION_ERROR':
      return { ...state, configurationError: action.payload, isLoadingConfiguration: false }
    
    case 'SET_ASSIGNED_USERS':
      return { 
        ...state, 
        assignedUsers: action.payload,
        isLoadingAssignedUsers: false,
        assignedUsersError: null
      }
    
    case 'SET_LOADING_ASSIGNED_USERS':
      return { ...state, isLoadingAssignedUsers: action.payload }
    
    case 'SET_ASSIGNED_USERS_ERROR':
      return { 
        ...state, 
        assignedUsersError: action.payload,
        isLoadingAssignedUsers: false
      }
    
    case 'SET_STAGE_FILTERS':
      return { ...state, stageFilters: action.payload, currentPage: 1, hasUnsavedChanges: true }

    case 'SET_GLOBAL_FILTERS':
      return { ...state, globalFilters: action.payload, currentPage: 1, hasUnsavedChanges: true }
    
    case 'CLEAR_ALL_FILTERS':
      // Clear the temporary query filter too — without this, "Clear Filters"
      // would leave cross-tab navigation filters (id eq 'X' from the VMS
      // email→linked-request flow, or FboAssignmentId eq … from the older
      // assignment-search flow) silently active and the user would have no
      // way to undo them.
      return {
        ...state,
        stageFilters: [],
        globalFilters: [],
        filters: {},
        temporaryQueryFilter: null,
        temporaryQueryFilterLabel: null,
        temporaryQueryFilterPausesStageFilters: false,
        currentPage: 1,
      }
    
    case 'SET_FACETS':
      return { ...state, facets: action.payload }
    
    case 'SET_STAGE_COUNTS':
      return { ...state, stageCounts: action.payload }
    
    case 'SET_CODE_VALUES_DATA':
      return { 
        ...state, 
        codeValuesData: action.payload,
        isLoadingCodeValues: false,
        codeValuesError: null
      }
    
    case 'SET_LOADING_CODE_VALUES':
      return { ...state, isLoadingCodeValues: action.payload }
    
    case 'SET_CODE_VALUES_ERROR':
      return { 
        ...state, 
        codeValuesError: action.payload,
        isLoadingCodeValues: false
      }
    
    case 'SET_MESSAGE_NOTIFICATIONS':
      // Lives in shared state, not in the hook, so the modal, the menu label and every row badge
      // all move together. Local hook state would only update whichever copy did the save.
      return { ...state, messageNotificationsEnabled: action.payload }
    case 'SET_COLUMN_WIDTHS':
      return { 
        ...state, 
        columnWidths: action.payload,
        hasUnsavedChanges: true
      }

    case 'SET_PINNED_COLUMN':
      return {
        ...state,
        pinnedColumnsByStage: {
          ...(state.pinnedColumnsByStage || {}),
          [action.payload.stageId]: action.payload.columnId,
        },
        hasUnsavedChanges: true,
      }
    case 'SET_PINNED_COLUMNS_BY_STAGE':
      return {
        ...state,
        pinnedColumnsByStage: action.payload,
      }
    
    case 'SET_VIEWS_RESPONSE':
      return { ...state, viewsResponse: action.payload }
    
    case 'CACHE_CLIENT_CUSTOM_FIELDS': {
      const newCache = new Map(state.cachedClientCustomFields)
      newCache.set(action.payload.clientFboId, action.payload.customFields)
      return {
        ...state,
        cachedClientCustomFields: newCache
      }
    }

    case 'APPLY_CUSTOM_FIELDS': {
      if (!state.configuration) return state

      const { customFields, clientFboId, clientName } = action.payload
      
      // Store original configuration if not already stored
      const originalConfig = state.originalConfiguration || state.configuration

      // Helper function to apply custom fields to field configurations
      const applyCustomFieldsToFields = (fields: FieldConfiguration[]): FieldConfiguration[] => {
        return fields.map(field => {
          if (field.mappingSource === 'FboCustomField' && field.mappingSourceTargetColumn) {
            const customField = customFields.find(cf => cf.FieldName === field.mappingSourceTargetColumn)
            if (customField) {
              return {
                ...field,
                columnNameEnglish: customField.DisplayName,
                columnNameFrench: customField.DisplayName,
                // Explicitly set customValues - either parsed array or empty array to clear stale values
                customValues: customField.SelectValues 
                  ? customField.SelectValues.split(',').map(val => ({
                      code: val.trim(),
                      titleEnglish: val.trim(),
                      titleFrench: val.trim(),
                      color: '',
                      isDeleted: false
                    }))
                  : []
              }
            }
          }
          return field
        })
      }

      // Update configuration - IMPORTANT: Apply to originalConfig, not current state.configuration
      // This ensures when switching from Client A to Client B, we start fresh from the original
      const updatedConfig = {
        ...originalConfig,
        fieldsInUse: applyCustomFieldsToFields(originalConfig.fieldsInUse),
        stageConfigurations: originalConfig.stageConfigurations.map(stage => ({
          ...stage,
          fieldConfigurations: applyCustomFieldsToFields(stage.fieldConfigurations)
        }))
      }

      return {
        ...state,
        configuration: updatedConfig,
        originalConfiguration: originalConfig,
        activeClientFboId: clientFboId,
        activeClientName: clientName,
        customFieldsApplied: true
      }
    }

    case 'CLEAR_CUSTOM_FIELDS': {
      if (!state.configuration || !state.originalConfiguration) return state
      
      // Helper function to revert custom field changes without removing fields from stages
      const revertCustomFieldsInFields = (fields: FieldConfiguration[]): FieldConfiguration[] => {
        return fields.map(field => {
          if (field.mappingSource === 'FboCustomField' && field.mappingSourceTargetColumn) {
            // Find the original field configuration
            const originalField = state.originalConfiguration?.fieldsInUse.find(
              f => f.fieldConfigurationId === field.fieldConfigurationId
            )
            if (originalField) {
              return {
                ...field,
                columnNameEnglish: originalField.columnNameEnglish,
                columnNameFrench: originalField.columnNameFrench,
                customValues: originalField.customValues
              }
            }
          }
          return field
        })
      }

      // Revert only the custom field properties, keep the stage structure
      const revertedConfig = {
        ...state.configuration,
        fieldsInUse: revertCustomFieldsInFields(state.configuration.fieldsInUse),
        stageConfigurations: state.configuration.stageConfigurations.map(stage => ({
          ...stage,
          fieldConfigurations: revertCustomFieldsInFields(stage.fieldConfigurations)
        }))
      }

      return {
        ...state,
        configuration: revertedConfig,
        activeClientFboId: null,
        activeClientName: null,
        customFieldsApplied: false
      }
    }
    
    case 'SET_HANDLING_CLIENT_FILTER_CHANGE':
      return { ...state, isHandlingClientFilterChange: action.payload }
    
    case 'APPLY_ASSIGNMENT_SEARCH':
      return {
        ...state,
        currentStage: action.payload.stage as WorkflowStage,
        stageFilters: action.payload.stageFilters,
        temporaryQueryFilter: `FboAssignmentId eq ${action.payload.assignmentId}`,
        // The stage filters set above ARE this scope, so they must keep applying.
        temporaryQueryFilterPausesStageFilters: false,
        currentPage: 1,
        selectedItems: [],
        loadingState: LoadingState.LOADING_WORK_ORDERS
      }
    
    case 'APPLY_TEMPORARY_QUERY_FILTER':
      // Generic temporary filter — caller supplies the full OData clause
      // (e.g. "id eq 'X'") plus an optional human-readable label that the
      // grid surfaces as a dismissible chip. Used by cross-grid navigation
      // flows that don't fit the APPLY_ASSIGNMENT_SEARCH shape (which is
      // FboAssignmentId-specific). Resets the grid to page 1 because the
      // result set is narrowing — staying on a higher page often renders
      // empty.
      return {
        ...state,
        temporaryQueryFilter: action.payload.filter,
        temporaryQueryFilterLabel: action.payload.label ?? null,
        temporaryQueryFilterPausesStageFilters: action.payload.pauseStageFilters ?? false,
        currentPage: 1,
        selectedItems: [],
      }

    case 'CLEAR_TEMPORARY_FILTER':
      return {
        ...state,
        temporaryQueryFilter: null,
        temporaryQueryFilterLabel: null,
        temporaryQueryFilterPausesStageFilters: false,
      }

    case 'SET_SHOW_COMPLETED':
      return {
        ...state,
        showCompletedByStage: {
          ...(state.showCompletedByStage || {}),
          [action.payload.stageId]: action.payload.showCompleted,
        },
      }

    case 'SET_SORT_STATE':
      return {
        ...state,
        sortByStage: {
          ...(state.sortByStage || {}),
          [action.payload.stageId]: action.payload.sort,
        },
      }

    default:
      return state
  }
}

interface WorkflowContextType {
  // State
  state: WorkflowState
  currentUser: User
  currentStage?: WorkflowStage
  isFilterDrawerOpen: boolean
  isBulkEditModalOpen: boolean
  isLoadingColumns: boolean
  savedViews: SavedView[]
  currentView: SavedView | null
  hasUnsavedChanges: boolean
  filters: Filters
  
  // View data from API
  viewsResponse: UserViewsResponse | null
  
  // Data properties for WorkflowGrid
  workOrders: WorkflowItem[] // Alias for workflowItems
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  columns: FieldConfiguration[]
  visibleColumns: FieldConfiguration[]
  selectedItems: string[]
  currentPage: number
  pageSize: number
  
  // Loading states
  isLoadingWorkOrders: boolean
  isLoadingRows: boolean
  
  // Actions
  setCurrentStage: (stage: WorkflowStage) => void
  toggleFilterDrawer: () => void
  updateFilter: (key: string, value: any) => void
  clearFilters: () => void
  setSelectedItems: (items: string[]) => void
  toggleItemSelection: (itemId: string) => void
  selectAllItems: () => void
  deselectAllItems: () => void
  setCurrentPage: (page: number) => void
  setPageSize: (size: number) => void
  setItemsPerPage: (itemsPerPage: number | null) => void
  updateVisibleColumns: (columns: FieldConfiguration[]) => void
  
  // Data operations
  /**
   * Re-run the grid search query.
   * @param options.forceLoadingState — flip loadingState to LOADING_WORK_ORDERS
   *   unconditionally. Use for user-initiated refreshes (header Refresh icon)
   *   so the spinner is visible regardless of how fast the network responds.
   */
  refreshData: (customFilterArray?: string[], orderBy?: string, facetKeys?: string[], targetStage?: WorkflowStage, options?: { forceLoadingState?: boolean; silent?: boolean }) => Promise<void>
  updateWorkOrderField: (orderId: string, field: string, value: any) => Promise<boolean>
  updateWorkOrderFieldOptimistically: (orderId: string, field: string, value: any) => void
  removeWorkOrder: (orderId: string) => void
  removeWorkOrdersBulk: (orderIds: string[]) => void
  bulkUpdateWorkOrderField: (field: string, value: any) => Promise<void>
  applyOptimisticUpdates: (updates: { id: string; updates: Record<string, any> }[], options?: { silent?: boolean }) => void
  bulkAssignWorkOrders: (userNames: string[]) => Promise<void>
  
  // Modal operations
  openBulkEditModal: (field: string) => void
  closeBulkEditModal: () => void
  
  // View operations
  setCurrentView: (view: SavedView) => Promise<void>
  setSavedViews: (views: SavedView[]) => void
  setUnsavedChanges: (hasChanges: boolean) => void
  saveCurrentView: () => Promise<void>
  saveViewAs: (name: string, isPublic?: boolean, visibleInApplications?: string[], allowDataExport?: boolean) => Promise<SavedView>
  deleteView: (viewId: string) => Promise<void>
  updateViewMetadata: (title: string, isPublic: boolean, isTitleAndVisibilityUpdate?: boolean, saveFilters?: boolean, visibleInApplications?: string[]) => Promise<void>
  refreshViews: () => Promise<UserViewsResponse | null>
  
  // Column operations
  addCustomColumn: (column: FieldConfiguration) => void
  deleteColumn: (columnId: string) => void

  // Configuration operations
  refreshConfiguration: (force?: boolean, viewId?: string | null, skipFilterUpdate?: boolean, preferUserState?: boolean) => Promise<void>
  updateConfigurationDirectly: (config: WorkflowConfiguration) => void
  configuration: WorkflowConfiguration | undefined
  isLoadingConfiguration: boolean
  configurationError: string | null

  // Stages
  stages: StageConfiguration[]

  // Assigned Users operations
  refreshAssignedUsers: () => Promise<void>
  assignedUsers: AssignedUser[]
  isLoadingAssignedUsers: boolean
  assignedUsersError: string | null

  // Facets
  facets?: Record<string, { value: string; count: number }[]>

  // Stage tab counts
  stageCounts?: Record<string, number>
  setStageCounts: (counts: Record<string, number>) => void

  // New operations
  setStageFilters: (filters: { stageId: string; fieldConfigurationId: string; filter: string; values?: any }[]) => void
  setGlobalFilters: (filters: { fieldConfigurationId: string; filter: string; values?: string }[]) => void
  clearAllFilters: () => void
  applyAssignmentSearch: (stage: string, assignmentId: number, stageFilters: any[]) => void
  /**
   * Set an arbitrary OData filter as the grid's temporary query. Used by
   * cross-grid navigation flows (e.g. VmsEmail → VmsRequest "open linked
   * request") that need to scope the destination grid to a specific row
   * via `id eq 'X'` without piggybacking on the assignment-search shape.
   * Clear via <see cref="clearTemporaryFilter"/>.
   */
  /**
   * Apply a temporary OData query filter to the active grid. `filter` is the
   * raw clause (e.g. "id eq 'X'") and `label` is the optional friendly text
   * surfaced in the dismissible chip above the grid.
   */
  applyTemporaryQueryFilter: (
    filter: string,
    label?: string,
    options?: { pauseStageFilters?: boolean },
  ) => void
  clearTemporaryFilter: () => void

  // New state for stageFilters and globalFilters
  stageFilters: { stageId: string; fieldConfigurationId: string; filter: string; values?: any }[]
  globalFilters: { fieldConfigurationId: string; filter: string; values?: string }[]
  temporaryQueryFilter?: string | null
  /** Friendly label shown in the temp-filter chip. Null when no label was supplied. */
  temporaryQueryFilterLabel?: string | null
  /** True when this scope should also pause the stage's saved column filters. */
  temporaryQueryFilterPausesStageFilters?: boolean

  // New state for work orders facets
  workOrdersFacets: Record<string, { value: string; count: number }[]> | undefined

  // Code Values
  codeValues: ReturnType<typeof useCodeValues>
  setCodeValues: (codeValues: any) => void
  
  // Code Values State
  codeValuesData?: Record<string, any[]>
  isLoadingCodeValues: boolean
  codeValuesError: string | null
  refreshCodeValues: () => Promise<void>

  // Column Widths operations
  setColumnWidths: (columnWidths: Record<string, number>) => void
  setMessageNotificationsEnabled: (enabled: boolean) => void

  // Pinned column operations
  setPinnedColumn: (columnId: string | null) => void

  // Sort state per stage
  sortByStage: Record<string, { columnCode: string; direction: 'asc' | 'desc' } | null>
  setSortForStage: (sort: { columnCode: string; direction: 'asc' | 'desc' } | null) => void

  // Save view state silently (for visibility updates, etc.)
  saveViewStateSilently: (updatedPinnedColumns?: Record<string, string | null>, viewsResponseOverride?: UserViewsResponse | null, updatedSortByStage?: Record<string, { columnCode: string; direction: 'asc' | 'desc' } | null>) => Promise<void>

  // Show-completed toggle per stage
  showCompletedByStage: Record<string, boolean>
  setShowCompleted: (stageId: string, showCompleted: boolean) => void
}

interface WorkflowProviderProps {
  children: ReactNode
  /**
   * Pre-fetched configuration to seed the provider's state with, in lieu of
   * the mount-time GET /configurations request. Used by the VMS tab — the
   * parent (useVmsConfig) fetches both VmsRequest + VmsEmail configurations
   * in parallel BEFORE the per-sub-tab WorkflowProviders mount, so when those
   * providers do mount they'd otherwise re-fetch the exact same URLs and
   * miss the coalesce window (entries are deleted the moment the original
   * promise settles). Passing the resolved configuration here skips the
   * duplicate fetch entirely.
   *
   * Leave undefined (the default) and the provider falls back to its normal
   * mount-time fetch — the original behaviour for every non-VMS code path.
   */
  initialConfiguration?: WorkflowConfiguration | null

  /**
   * Opt this provider out of the cross-tab RequestsGroupStateProvider store.
   *
   * Set it on any SECONDARY / overlay mount that shares an entityType with a live tab
   * provider — today that's the hub-level View Designer, which mounts its own
   * WorkflowProvider under a controlled EntityTypeProvider so its API calls carry the
   * active tab's entityType. `tabs` is keyed by entityType ALONE and last-write-wins, and
   * `clearTab` is never called, so a second publisher permanently overwrites the real
   * tab's snapshot: the designer would publish `hasUnsavedChanges: false` plus its own
   * freshly-fetched (default) stageConfigurations, and a later cross-tab group save would
   * write that stale snapshot onto the sibling's ViewSettings doc — silently discarding the
   * user's column layout.
   *
   * Also skips the refreshSignal subscription, so sibling saves don't drive a pointless
   * refreshConfiguration inside a throwaway provider.
   */
  suppressRequestsGroupPublish?: boolean
}

export function WorkflowProvider({ children, initialConfiguration, suppressRequestsGroupPublish = false }: WorkflowProviderProps) {
  const [state, dispatch] = useReducer(workflowReducer, initialState)
  const workflowApi = useWorkflowApi()
  const { entityType: contextEntityType } = useEntityType()
  // Tracks entity types served by the shared client-request index (and any other
  // index that doesn't follow the WorkOrder Assignment+Stage filter shape).
  // ClientRequest joined this group when its grid swapped from the bespoke
  // client-requests-grid to the config-driven WorkflowGrid — its docs use the
  // same `DocumentType eq '<entityType>'` discriminator the VMS sub-tabs use,
  // not the WorkOrder `Assignment + Stage` prefix. The name is kept for blame
  // continuity; rename to e.g. `isNonAssignmentEntity` when ATS / PreOnboard
  // join.
  const isRequestTabEntityType = contextEntityType === 'VmsRequest' 
    || contextEntityType === 'VmsEmail' || contextEntityType === 'ClientRequest' 
    || contextEntityType === 'AtsPlacementRequest'
  const { token, isAuthenticated } = useAuth()
  const { toast } = useToast()
  const codeValues = useCodeValues()

  // Live ref to the Requests-group shared store. Callbacks (saveCurrentView,
  // saveViewAs, deleteView, …) need to bump the refresh signal after a
  // successful save / delete so sibling providers reload. Reading via ref
  // means we don't have to add `requestsGroupState` to every callback's dep
  // array — its `value` object changes on every snapshot publish, which
  // would otherwise rebuild every callback constantly.
  //
  // The actual useRequestsGroupState() subscription happens further down
  // (with `requestsGroupState = useRequestsGroupState()`) where we need
  // the live value for the publish + refresh-subscribe effects. This ref
  // is updated from that same value below.
  const requestsGroupStateRef = useRef<ReturnType<typeof useRequestsGroupState>>(null)

  // Pinned columns come from configuration; no local persistence required

  // Fetch views on component mount
  useEffect(() => {
    const loadViews = async () => {
      if (!isAuthenticated || !token) {
        return
      }
      
      try {
        const viewsResponse = await workflowApi.fetchViews()
        
        // Store the original API response structure
        const allViews: SavedView[] = []
        
        if (viewsResponse.defaultView) {
          allViews.push(viewsResponse.defaultView)
        }
        
        allViews.push(...viewsResponse.userViews)
        allViews.push(...viewsResponse.sharedViews)
        
        // Set the default view as current view
        const currentView = viewsResponse.defaultView || null
        
        dispatch({ type: 'SET_SAVED_VIEWS', payload: allViews })
        dispatch({ type: 'SET_CURRENT_VIEW', payload: currentView })
        dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })
      } catch (error: any) {
        console.error('Failed to fetch views:', error)
        // Keep empty state if fetch fails
      }
    }

    loadViews()
  }, [isAuthenticated, token]) // Add authentication dependencies

  // Load columns dynamically from stageConfiguration
  const loadColumns = useCallback(() => {
    if (!state.configuration || !state.currentStage) return;
    const currentStageConfig = state.stages.find(
      s => s.stageCode.toLowerCase() === state.currentStage!.toLowerCase()
    );
    if (!currentStageConfig) return;

    // Use the actual fieldConfigurations as columns
    const columns = currentStageConfig.fieldConfigurations;
    const visibleColumns = columns;

    dispatch({ type: 'SET_COLUMNS', payload: { columns, visibleColumns } });
  }, [state.configuration, state.currentStage, state.stages]);

  // Refresh work order data
  const refreshData = useCallback(async (customFilterArray?: string[], orderBy?: string, facetKeys?: string[], targetStage?: WorkflowStage, options?: { forceLoadingState?: boolean; silent?: boolean }) => {
    if (!isAuthenticated || !token) {
      return;
    }
    try {
      // Loading state policy:
      //   - Default (forceLoadingState=false): only flip to LOADING_WORK_ORDERS
      //     when the grid is fully idle, so we don't stomp on in-flight
      //     UPDATING_WORK_ORDER / BULK_UPDATING indicators with a generic
      //     "loading rows" spinner.
      //   - forceLoadingState=true: explicit user gesture (e.g. the header
      //     Refresh icon). The spinner is the whole point of the affordance,
      //     so always flip to LOADING_WORK_ORDERS — otherwise a sub-100ms
      //     network response leaves the user staring at an unchanged grid
      //     wondering whether the click registered.
      //   - silent=true: background reconciliation after a mutation whose
      //     result is already on screen via the optimistic overlay. Never
      //     flip to LOADING_WORK_ORDERS — the whole-grid spinner reads as
      //     jank when the user already sees the updated value (and the
      //     timed safety refetches would flash it repeatedly).
      if (!options?.silent && (options?.forceLoadingState || state.loadingState === LoadingState.IDLE)) {
        dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.LOADING_WORK_ORDERS })
      }
      
      // Use targetStage if provided, otherwise use state.currentStage
      let currentStage = targetStage || state.currentStage
      if (!currentStage && state.stages.length > 0) {
        currentStage = resolveInitialStage(state.stages, contextEntityType)
        dispatch({ type: 'SET_CURRENT_STAGE', payload: currentStage })
      }
      
      if (!currentStage) {
        console.error("No current stage available")
        return;
      }
      
      // Find the current stage configuration
      const currentStageConfig = state.stages.find(s => s.stageCode === currentStage)
      if (!currentStageConfig) {
        throw new Error(`No stage configuration found for current stage '${currentStage}'. Available stages: ${state.stages.map(s => s.stageCode).join(', ')}`)
      }
      
      // Build select string from fieldConfigurations, excluding static fields
      let selectFields = currentStageConfig && currentStageConfig.fieldConfigurations.length > 0
        ? currentStageConfig.fieldConfigurations
            .filter(f => !f.isStaticField) // Filter out static fields
            .map(f => f.columnCode)
        : [];
      
      // Always include fields from configuration, or use default list if not provided
      const alwaysInclude = state.configuration?.alwaysFetchedFieldCodes || ["FboId", "ObId", "RecordType", "InformationRequestType"];
      for (const field of alwaysInclude) {
        if (!selectFields.includes(field)) {
          selectFields.push(field);
        }
      }

      // Include fields referenced in actionConfigurations cell-link entries (columnCode set)
      for (const action of state.configuration?.actionConfigurations ?? []) {
        if (action.columnCode && action.link) {
          const matches = [...action.link.matchAll(/\$\{(\w+)\}/g)];
          for (const match of matches) {
            if (!selectFields.includes(match[1])) {
              selectFields.push(match[1]);
            }
          }
        }
      }

      const select = selectFields.length > 0 ? selectFields.join(",") : "*";
      
      // Determine page size based on itemsPerPage setting
      const effectivePageSize = state.itemsPerPage !== null ? state.itemsPerPage : state.pageSize;
      
      // Include temporary query filter if present
      let effectiveFilterArray = customFilterArray || [];
      if (state.temporaryQueryFilter) {
        effectiveFilterArray = [...effectiveFilterArray, state.temporaryQueryFilter];
      }

      // Fixed-filter stages supply their own complete OData scope — pass undefined
      // as the stage so buildFilterFromStageAndFilters skips the default
      // "DocumentType eq 'Assignment' and Stage eq '...'" prefix.
      // When no customFilterArray was provided (e.g. initial load / plain refreshData()),
      // we must inject the fixedFilter ourselves; callers that pre-build the full filter
      // already include it in the customFilterArray they pass in.
      if (!customFilterArray && currentStageConfig.fixedFilter) {
        // Mirror triggerDataFetch: an active temporary filter reveals completed
        // rows transiently (row-scope navigation must show terminal rows)
        // without touching the persisted Show Completed toggle.
        const currentShowCompleted = ((state.showCompletedByStage || {})[currentStageConfig.stageId] ?? false)
          || !!state.temporaryQueryFilter
        const resolvedFixedFilter = currentShowCompleted
          ? currentStageConfig.fixedFilter
          : `${currentStageConfig.fixedFilter} and OnboardingStatus ne 'Completed'`
        effectiveFilterArray = [resolvedFixedFilter, ...effectiveFilterArray]
      }

      const stageArg = currentStageConfig.fixedFilter ? undefined : currentStage

      // API call: Search workflow items with current stage and pagination
      const response = await workflowApi.searchWorkflowItems(
        stageArg,
        effectiveFilterArray,
        currentStageConfig.fieldConfigurations,
        state.currentPage,
        effectivePageSize,
        '*', // search term
        select,
        undefined, // filter
        orderBy,
        facetKeys
      )
      
      // Response received: Update state with workflow data
      dispatch({ type: 'SET_WORK_ORDERS', payload: response })
    } catch (error) {
      console.error('Failed to refresh data:', error)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load workflow data' })
    }
  }, [state.currentStage, state.currentPage, state.pageSize, state.itemsPerPage, state.stages, state.temporaryQueryFilter, state.showCompletedByStage, isAuthenticated, token, contextEntityType])

  // Track if initial data has been loaded
  const hasInitialDataLoaded = useRef(false)

  // Initialize when configuration is ready:
  //   1. Set currentStage (so WorkflowHub renders the grid)
  //   2. Load columns
  // Data fetching is handled by workflow-grid's main effect (triggerDataFetch).
  useEffect(() => {
    if (!state.configuration) return;
    if (hasInitialDataLoaded.current) return;
    hasInitialDataLoaded.current = true;

    // Set initial stage — without this, WorkflowHub returns null and the grid never mounts.
    // Previously this was done inside refreshData(), but now the grid owns data fetching.
    if (!state.currentStage && state.stages.length > 0) {
      dispatch({ type: 'SET_CURRENT_STAGE', payload: resolveInitialStage(state.stages, contextEntityType) })
    }

    loadColumns();
  }, [state.configuration, loadColumns, contextEntityType]);



  // Update a single work order field
  const updateWorkOrderField = async (orderId: string, field: string, value: any): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.UPDATING_WORK_ORDER })
      
      // Find the item to get its full data (needed for V2 to determine correct ID)
      const item = state.workflowItems.find((wo: any) => wo.id === orderId);
      if (!item) {
        console.error('Item not found:', orderId);
        dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
        return false;
      }
      
      // Call the API first - NO optimistic update (V2: pass full item)
      const result = await workflowApi.updateWorkflowItemField(item, field, value)
      
      // Handle the response structure
      const { successUpdateCount, failedUpdateCount, messages } = result;
      
      if (successUpdateCount > 0) {
        // Success: Update the UI with the new value
        dispatch({
          type: 'UPDATE_WORK_ORDER_FIELD',
          payload: { orderId, field, value }
        })

        // If assigning a user, ensure they appear in the assignedUsers list for display
        if (field.toLowerCase() === 'assignedtouser' && value?.email) {
          const exists = state.assignedUsers.some(
            (u: any) => u.email?.toLowerCase() === value.email.toLowerCase()
          );
          if (!exists) {
            dispatch({
              type: 'SET_ASSIGNED_USERS',
              payload: [...state.assignedUsers, { email: value.email, fullName: value.fullName || value.email }]
            });
          }
        }

        dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
        return true; // Success
      }
      
      if (failedUpdateCount > 0) {
        // Failed: Show error message (UI was never changed, so no need to revert)
        const errorDetails = messages && messages.length > 0 
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}` 
          : '';
        
        toast({
          variant: "destructive",
          title: "Update Failed",
          description: `Failed to update field${errorDetails}`,
        });
      }
      
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
      return false; // Failed

    } catch (error) {
      console.error('Failed to update work order field:', error)
      
      // Exception: Show error message (UI was never changed, so no need to revert or refresh)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update field' })
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update field",
      })
      
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
      return false; // Failed
    }
  }

  // Optimistic single field update - updates UI immediately without waiting for API
  const updateWorkOrderFieldOptimistically = (orderId: string, field: string, value: any) => {
    try {
      // Find the item to get its full data (needed for V2 to determine correct ID)
      const item = state.workflowItems.find((wo: any) => wo.id === orderId);
      if (!item) {
        console.error('Item not found:', orderId);
        return;
      }
      
      // Update local state immediately AND record the field as pending (so a background
      // refresh won't clobber it before the server/index confirm, and so we can revert
      // to the prior value if the API call fails).
      dispatch({
        type: 'APPLY_OPTIMISTIC_UPDATES',
        payload: { updates: [{ id: orderId, updates: { [field]: value } }] }
      })

      // Call the API in the background without waiting (V2: pass full item)
      workflowApi.updateWorkflowItemField(item, field, value)
        .then((result) => {
          // Treat an all-failed response the same as a thrown error: revert + notify.
          if (result && typeof result.failedUpdateCount === 'number'
              && result.failedUpdateCount > 0 && (result.successUpdateCount ?? 0) === 0) {
            dispatch({ type: 'REVERT_OPTIMISTIC', payload: { id: orderId, fields: [field] } })
            toast({
              variant: "destructive",
              title: "Update Failed",
              description: (result.messages && result.messages.length > 0)
                ? result.messages.join('\n')
                : "Failed to update field",
            })
          }
        })
        .catch((error) => {
          // Revert the optimistic value to its prior state on error.
          dispatch({ type: 'REVERT_OPTIMISTIC', payload: { id: orderId, fields: [field] } })
          toast({
            variant: "destructive",
            title: "Error",
            description: error instanceof Error ? error.message : "Failed to update field",
          })
        })
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update field",
      })
    }
  }

  // Bulk update work order field
  const bulkUpdateWorkOrderField = async (field: string, value: any) => {
    if (state.selectedItems.length === 0) return

    try {
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.BULK_UPDATING })
      
      await workflowApi.bulkUpdateWorkflowItems(state.selectedItems, field, value)
      
      // Update local state for all selected items
      state.selectedItems.forEach(orderId => {
        dispatch({ 
          type: 'UPDATE_WORK_ORDER_FIELD', 
          payload: { orderId, field, value } 
        })
      })
      
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
      
    } catch (error) {
      console.error('Failed to bulk update work order field:', error)
      dispatch({ type: 'SET_ERROR', payload: 'Failed to bulk update field' })
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to bulk update field",
      })
    }
  }

  // Optimistic bulk update function - updates UI immediately without server refresh
  const applyOptimisticUpdates = (updates: { id: string; updates: Record<string, any> }[], options?: { silent?: boolean }) => {
    try {
      // Check if we're already processing an update to prevent duplicates
      if (state.loadingState === LoadingState.BULK_UPDATING) {
        return;
      }
      
      // Update local state immediately
      dispatch({
        type: 'APPLY_OPTIMISTIC_UPDATES',
        payload: { updates }
      })

      // If any update sets assignedToUser, ensure new users appear in the assignedUsers list
      const newUsers: { email: string; fullName: string }[] = [];
      for (const u of updates) {
        const assignedTo = u.updates.assignedToUser || u.updates.AssignedToUser;
        if (assignedTo?.email) {
          const exists = state.assignedUsers.some(
            (au: any) => au.email?.toLowerCase() === assignedTo.email.toLowerCase()
          ) || newUsers.some(nu => nu.email.toLowerCase() === assignedTo.email.toLowerCase());
          if (!exists) {
            newUsers.push({ email: assignedTo.email, fullName: assignedTo.fullName || assignedTo.email });
          }
        }
      }
      if (newUsers.length > 0) {
        dispatch({ type: 'SET_ASSIGNED_USERS', payload: [...state.assignedUsers, ...newUsers] });
      }

      // Single-row / status-flip call sites pass { silent: true } because they raise their own
      // success toast — without this they'd get a second, mislabeled "Bulk update successful" toast
      // for what was a single-row action. The bulk-edit modal omits the option and keeps this toast.
      if (!options?.silent) {
        toast({
          variant: "success",
          title: "Bulk update successful",
          description: `Updated ${updates.length} items successfully.`,
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update items",
      })
    }
  }

  // Bulk assign work orders
  const bulkAssignWorkOrders = async (userNames: string[]) => {
    if (state.selectedItems.length === 0) return

    const assignedToName = userNames[0];
    if (!assignedToName) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No assignee selected.',
      });
      return;
    }

    const itemsToProcess = [...state.selectedItems];
    const processedStage = state.currentStage;

    try {
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.BULK_UPDATING })

      const itemsSucceeded: string[] = [];
      const itemsFailed: string[] = [];

      for (const orderId of itemsToProcess) {
        const item = state.workflowItems.find((wo: any) => wo.id === orderId);
        if (!item) { itemsFailed.push(orderId); continue; }
        try {
          const result = await workflowApi.updateWorkflowItemField(item, 'assignedToUserFullName', assignedToName);
          if (result?.successUpdateCount > 0) {
            itemsSucceeded.push(orderId);
          } else {
            itemsFailed.push(orderId);
          }
        } catch {
          itemsFailed.push(orderId);
        }
      }

      itemsSucceeded.forEach(orderId => {
        dispatch({ type: 'REMOVE_WORK_ORDER', payload: orderId })
      })

      if (itemsFailed.length > 0) {
        dispatch({ type: 'SET_SELECTED_ITEMS', payload: itemsFailed })
      } else {
        dispatch({ type: 'SET_SELECTED_ITEMS', payload: [] })
      }

      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })

      if (itemsSucceeded.length === 0) {
        toast({
          variant: 'destructive',
          title: 'Assignment Failed',
          description: `Failed to assign ${itemsFailed.length} item(s) to ${assignedToName}`,
        });
        return;
      }

      const processingDescription = itemsSucceeded.length === 1
        ? 'Applying your changes, this may take a moment...'
        : `Applying changes to ${itemsSucceeded.length} items, this may take a moment...`;

      const { id: processingToastId, update: updateProcessingToast, dismiss: dismissProcessingToast } = toast({
        variant: 'default',
        title: 'Processing...',
        description: processingDescription,
      })

      setTimeout(() => {
        if (stateRef.current.currentStage !== processedStage) return;
        refreshData();
        updateProcessingToast({
          id: processingToastId,
          variant: 'success',
          title: itemsFailed.length > 0 ? 'Partially Assigned' : 'Success',
          description: itemsFailed.length > 0
            ? `Assigned ${itemsSucceeded.length} item(s) to ${assignedToName}; ${itemsFailed.length} failed.`
            : `Assigned ${itemsSucceeded.length} item(s) to ${assignedToName}`,
        });
      }, 5000);

      setTimeout(() => { dismissProcessingToast(); }, 12000);

    } catch (error) {
      console.error('Failed to bulk assign work orders:', error)
      dispatch({ type: 'SET_LOADING_STATE', payload: LoadingState.IDLE })
      dispatch({ type: 'SET_ERROR', payload: 'Failed to bulk assign work orders' })
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to bulk assign work orders",
      })
    }
  }

  // Modal operations
  const openBulkEditModal = useCallback((field: string) => {
    dispatch({ type: 'OPEN_BULK_EDIT_MODAL', payload: field })
  }, [dispatch])

  const closeBulkEditModal = useCallback(() => {
    dispatch({ type: 'CLOSE_BULK_EDIT_MODAL' })
  }, [dispatch])

  const setSavedViews = useCallback((views: SavedView[]) => {
    dispatch({ type: 'SET_SAVED_VIEWS', payload: views })
  }, [dispatch])

  const setUnsavedChanges = useCallback((hasChanges: boolean) => {
    dispatch({ type: 'SET_UNSAVED_CHANGES', payload: hasChanges })
  }, [dispatch])

  // Column operations
  const addCustomColumn = useCallback((column: FieldConfiguration) => {
    dispatch({ type: 'ADD_CUSTOM_COLUMN', payload: column })
  }, [dispatch])

  const deleteColumn = useCallback((columnId: string) => {
    dispatch({ type: 'DELETE_COLUMN', payload: columnId })
  }, [dispatch])

  // Save current view
  const saveCurrentView = useCallback(async () => {
    if (!state.currentView?.viewSettingsId) {
      return
    }

    try {
      // Build stage configurations from current state
      const stageConfigurations = state.stages.map(stage => ({
        stageConfigurationId: stage.stageId,
        fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
        pinnedColumnId: (state.pinnedColumnsByStage || {})[stage.stageId] || null,
        showCompleted: (state.showCompletedByStage || {})[stage.stageId] ?? false,
        sortColumn: (state.sortByStage || {})[stage.stageId]?.columnCode || null,
        sortDirection: (state.sortByStage || {})[stage.stageId]?.direction || null,
      }))

      // Serialize stage filters with values as JSON strings
      const serializedStageFilters = state.stageFilters.map(filter => ({
        ...filter,
        values: filter.values ? JSON.stringify(filter.values) : undefined
      }))

      // For default views, preserve title, isPublic, and isDefault from original view
      const isDefaultView = state.currentView.isDefault === true
      const request = {
        id: state.currentView.viewSettingsId,
        title: isDefaultView ? state.currentView.title : state.currentView.title,
        isDefault: isDefaultView ? state.currentView.isDefault : false, 
        isPublic: isDefaultView ? state.currentView.isPublic : (state.currentView.isPublic ?? false),
        sharedUserIds: [], // TODO: Get from UI when sharing is implemented
        stageConfigurations,
        stageFilters: serializedStageFilters,
        globalFilters: state.globalFilters,
        columnWidths: JSON.stringify(state.columnWidths || {}), // Always include column widths
        itemsPerPage: state.itemsPerPage,
        isUserViewStateUpdate: true,
        updateFilters: true
      }

      await workflowApi.saveViewSettings(request)

      // Refresh views after saving
      const viewsResponse = await workflowApi.fetchViews()
      dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })

      // Clear unsaved changes
      dispatch({ type: 'SET_UNSAVED_CHANGES', payload: false })

      // Notify sibling Requests-group providers to refresh their
      // configuration — BE save fan-out updated their UserViewState rows,
      // but already-mounted providers (e.g. the VMS Requests sub-tab while
      // user is on VMS Emails) don't see those changes until they remount.
      // Pass our entityType as the origin so we don't redundantly re-fetch
      // ourselves; we already have the latest data inline.
      if (isRequestsGroupEntity(contextEntityType)) {
        requestsGroupStateRef.current?.bumpRefreshSignal(contextEntityType as RequestsGroupEntityType)
      }
    } catch (error) {
      console.error('Failed to save current view:', error)
      throw error
    }
  }, [state.currentView, state.stages, state.stageFilters, state.globalFilters, state.showCompletedByStage, workflowApi, dispatch, contextEntityType])

  // Save view as
  const saveViewAs = useCallback(async (name: string, isPublic: boolean = false, visibleInApplications?: string[], allowDataExport: boolean = false) => {
    try {
      // Build stage configurations from current state. showCompleted is
      // included so the new view captures the user's current toggle state
      // — without it, "Save as new view" silently dropped the toggle and
      // the new view always loaded with showCompleted=false.
      const stageConfigurations = state.stages.map(stage => ({
        stageConfigurationId: stage.stageId,
        fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
        pinnedColumnId: (state.pinnedColumnsByStage || {})[stage.stageId] || null,
        sortColumn: (state.sortByStage || {})[stage.stageId]?.columnCode || null,
        sortDirection: (state.sortByStage || {})[stage.stageId]?.direction || null,
        showCompleted: (state.showCompletedByStage || {})[stage.stageId] ?? false,
      }))

      // Serialize stage filters with values as JSON strings
      const serializedStageFilters = state.stageFilters.map(filter => ({
        ...filter,
        values: filter.values ? JSON.stringify(filter.values) : undefined
      }))

      const request = {
        // Don't set id for "Save as new" - it will be created
        title: name,
        isDefault: false,
        isPublic: isPublic,
        sharedUserIds: [],
        stageConfigurations,
        stageFilters: serializedStageFilters,
        globalFilters: state.globalFilters,
        columnWidths: JSON.stringify(state.columnWidths || {}),
        itemsPerPage: state.itemsPerPage,
        isUserViewStateUpdate: true,
        visibleInApplications,
        // New views default AllowDataExport=false; the BE create path writes
        // request.AllowDataExport verbatim, so this must be sent or the flag is
        // dropped when an admin ticks "Allow data export" while creating a view.
        allowDataExport,
      }

      const response = await workflowApi.saveViewSettings(request)
      
      // Refresh views after saving
      const viewsResponse = await workflowApi.fetchViews()
      dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })
      
      // Clear unsaved changes
      dispatch({ type: 'SET_UNSAVED_CHANGES', payload: false })
      
      // Find the newly created view and return it
      const allViews: SavedView[] = []
      if (viewsResponse.defaultView) {
        allViews.push(viewsResponse.defaultView)
      }
      allViews.push(...viewsResponse.userViews)
      allViews.push(...viewsResponse.sharedViews)
      
      // Find the newly created view by matching title and isPublic, but exclude the current view
      const newlyCreatedView = allViews.find(view => 
        view.title === name && 
        view.isPublic === isPublic &&
        view.viewSettingsId !== state.currentView?.viewSettingsId
      )
      
      // Also update the savedViews state to ensure consistency
      dispatch({ type: 'SET_SAVED_VIEWS', payload: allViews })

      // After a successful "save as new", the BE created the active sibling
      // PLUS the other 3 group siblings (auto-grouping). Other mounted
      // providers in the group need to refresh so their views dropdown
      // includes the new sibling. Same skip-self semantics as saveCurrentView.
      if (isRequestsGroupEntity(contextEntityType)) {
        requestsGroupStateRef.current?.bumpRefreshSignal(contextEntityType as RequestsGroupEntityType)
      }

      return newlyCreatedView || response
    } catch (error) {
      console.error('Failed to save view as:', error)
      throw error
    }
  }, [state.stages, state.stageFilters, state.globalFilters, workflowApi, dispatch, contextEntityType])

  // Update view metadata (title and isPublic)
  const updateViewMetadata = useCallback(async (title: string, isPublic: boolean, isTitleAndVisibilityUpdate: boolean = false, saveFilters: boolean = false, visibleInApplications?: string[]) => {
    if (!state.currentView?.viewSettingsId) {
      return
    }

    try {
      // Build stage configurations from current state. showCompleted is
      // merged in alongside pin/sort so updating view metadata doesn't
      // silently overwrite the user's current toggle on the persisted
      // view doc.
      const stageConfigurations = state.stages.map(stage => ({
        stageConfigurationId: stage.stageId,
        fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
        pinnedColumnId: (state.pinnedColumnsByStage || {})[stage.stageId] || null,
        sortColumn: (state.sortByStage || {})[stage.stageId]?.columnCode || null,
        sortDirection: (state.sortByStage || {})[stage.stageId]?.direction || null,
        showCompleted: (state.showCompletedByStage || {})[stage.stageId] ?? false,
      }))

      // Serialize stage filters with values as JSON strings
      const serializedStageFilters = state.stageFilters.map(filter => ({
        ...filter,
        values: filter.values ? JSON.stringify(filter.values) : undefined
      }))

      const request = {
        id: state.currentView.viewSettingsId,
        title: title, // Use the new title
        isDefault: false, 
        isPublic: isPublic, // Use the new isPublic value
        sharedUserIds: [],
        stageConfigurations,
        stageFilters: serializedStageFilters,
        globalFilters: state.globalFilters,
        columnWidths: JSON.stringify(state.columnWidths || {}), // Always include column widths
        itemsPerPage: state.itemsPerPage,
        isTitleAndVisibilityUpdate: isTitleAndVisibilityUpdate,
        isUserViewStateUpdate: true,
        updateFilters: saveFilters,
        visibleInApplications
      }

      await workflowApi.saveViewSettings(request)

      // Refresh views after saving
      const viewsResponse = await workflowApi.fetchViews()
      dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })

      // Update current view with new metadata
      const updatedView = viewsResponse.userViews.find(v => v.viewSettingsId === state.currentView?.viewSettingsId) ||
                         viewsResponse.sharedViews.find(v => v.viewSettingsId === state.currentView?.viewSettingsId)

      if (updatedView) {
        dispatch({ type: 'SET_CURRENT_VIEW', payload: updatedView })
      }

      // Clear unsaved changes
      dispatch({ type: 'SET_UNSAVED_CHANGES', payload: false })

      // Title / visibility / filter changes propagate to sibling docs on
      // the BE. Bump the refresh signal so any sibling providers that are
      // currently mounted pick up the new metadata (new title in their
      // dropdown, updated visibility, etc.) without remounting.
      if (isRequestsGroupEntity(contextEntityType)) {
        requestsGroupStateRef.current?.bumpRefreshSignal(contextEntityType as RequestsGroupEntityType)
      }
    } catch (error) {
      console.error('Failed to update view metadata:', error)
      throw error
    }
  }, [state.currentView, state.stages, state.stageFilters, state.globalFilters, workflowApi, dispatch, contextEntityType])

  // Update configuration immediately without fetching from server
  const updateConfigurationDirectly = useCallback((config: WorkflowConfiguration) => {
    const configToDispatch = { ...config, savedGlobalFilters: undefined, savedStageFilters: undefined }
    dispatch({ type: 'SET_CONFIGURATION', payload: configToDispatch })
  }, [dispatch])

  // Configuration operations
  const refreshConfiguration = useCallback(async (force?: boolean, viewId?: string | null, skipFilterUpdate?: boolean, preferUserState?: boolean) => {
    if (!isAuthenticated || !token) {
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING_CONFIGURATION', payload: true })

      const configuration = await workflowApi.fetchWorkflowConfiguration(viewId, preferUserState)
      // skipFilterUpdate is the "soft refresh" signal — the caller did
      // something that triggered a config re-fetch (column reorder,
      // jump-to-column, edit field modal, …) but does NOT want unrelated
      // client-side state clobbered by stale or default values coming back
      // from the server. We:
      //   - strip savedGlobalFilters / savedStageFilters so the reducer
      //     leaves the current filter state alone
      //   - flag the payload with _preserveClientPrefs so the reducer
      //     keeps the current pinnedColumnsByStage + showCompletedByStage
      //     maps instead of rebuilding from the response. Underscore prefix
      //     keeps the marker namespaced from anything the server might add.
      const configToDispatch = skipFilterUpdate
        ? {
            ...configuration,
            savedGlobalFilters: undefined,
            savedStageFilters: undefined,
            _preserveClientPrefs: true,
          }
        : configuration
      dispatch({ type: 'SET_CONFIGURATION', payload: configToDispatch })
      
      // Set itemsPerPage from configuration if available
      if (configuration.itemsPerPage !== undefined) {
        dispatch({ type: 'SET_ITEMS_PER_PAGE', payload: configuration.itemsPerPage })
      }
      
      // Set current view based on viewSettingsId in configuration
      if (configuration.viewSettingsId && state.viewsResponse) {
        // Find the view that matches the viewSettingsId
        let targetView: SavedView | null = null;
        
        // Check default view
        if (state.viewsResponse.defaultView?.viewSettingsId === configuration.viewSettingsId) {
          targetView = state.viewsResponse.defaultView;
        }
        
        // Check user views
        if (!targetView) {
          targetView = state.viewsResponse.userViews.find(v => v.viewSettingsId === configuration.viewSettingsId) || null;
        }
        
        // Check shared views
        if (!targetView) {
          targetView = state.viewsResponse.sharedViews.find(v => v.viewSettingsId === configuration.viewSettingsId) || null;
        }
        
        // Check public views
        if (!targetView) {
          targetView = state.viewsResponse.publicViews.find(v => v.viewSettingsId === configuration.viewSettingsId) || null;
        }
        
        // Set the current view if found
        if (targetView) {
          dispatch({ type: 'SET_CURRENT_VIEW', payload: targetView });
        }
      }
    } catch (error) {
      console.error('Failed to refresh configuration:', error)
      dispatch({ type: 'SET_CONFIGURATION_ERROR', payload: 'Failed to load configuration' })
    }
  }, [isAuthenticated, token])

  // Assigned Users operations. Pass the current entity type so the backend
  // queries the matching Cosmos container — without it, VMS tabs would
  // surface WorkOrder/Assignment assignees because the legacy endpoint is
  // hardcoded to that container.
  const refreshAssignedUsers = useCallback(async () => {
    if (!isAuthenticated || !token) {
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING_ASSIGNED_USERS', payload: true })

      const assignedUsers = await workflowApi.fetchAssignedUsers(contextEntityType ?? undefined)
      dispatch({ type: 'SET_ASSIGNED_USERS', payload: assignedUsers })
    } catch (error) {
      console.error('Failed to refresh assigned users:', error)
      dispatch({ type: 'SET_ASSIGNED_USERS_ERROR', payload: 'Failed to load assigned users' })
    }
  }, [isAuthenticated, token, contextEntityType])

  // View operations
  const setCurrentView = useCallback(async (view: SavedView) => {
    dispatch({ type: 'SET_CURRENT_VIEW', payload: view })
    
    // Clear unsaved changes when switching views
    dispatch({ type: 'SET_UNSAVED_CHANGES', payload: false })
    
    // First fetch configuration with the new view ID and wait for it to complete
    const configuration = await workflowApi.fetchWorkflowConfiguration(view.viewSettingsId)
    dispatch({ type: 'SET_CONFIGURATION', payload: configuration })
    
    // Then call POST view-state endpoint with the freshly loaded configuration
    try {
      // Build stage configurations from the freshly fetched configuration
      // (not old state.stages). showCompleted is sourced from the same
      // configuration response — the backend merge in
      // GetWorkflowHubConfigurations populates TenantStageConfigurationDto
      // .ShowCompleted from the view's saved state, so we just pass it
      // through. Omitting it here would round-trip stale-zero into the
      // newly-active view doc and clear the toggle on every view switch.
      const stageConfigurations = (configuration?.stageConfigurations || []).map(stage => ({
        stageConfigurationId: stage.stageId,
        fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
        pinnedColumnId: stage.pinnedColumnId ?? null,
        sortColumn: (stage as any).sortColumn ?? null,
        sortDirection: (stage as any).sortDirection ?? null,
        showCompleted: (stage as any).showCompleted ?? false,
      }))

      // Get all visible view IDs from the views response
      const visibleViewIds: string[] = [];
      
      // Add default view if it exists and is visible
      if (state.viewsResponse?.defaultView && state.viewsResponse.defaultView.isVisible !== false) {
        visibleViewIds.push(state.viewsResponse.defaultView.viewSettingsId);
      }
      
      // Add user views that are visible
      if (state.viewsResponse?.userViews) {
        state.viewsResponse.userViews.forEach(view => {
          if (view.isVisible !== false) {
            visibleViewIds.push(view.viewSettingsId);
          }
        });
      }
      
      // Add shared views that are visible
      if (state.viewsResponse?.sharedViews) {
        state.viewsResponse.sharedViews.forEach(view => {
          if (view.isVisible !== false) {
            visibleViewIds.push(view.viewSettingsId);
          }
        });
      }
      
      // Add public views that are visible
      if (state.viewsResponse?.publicViews) {
        state.viewsResponse.publicViews.forEach(view => {
          if (view.isVisible !== false) {
            visibleViewIds.push(view.viewSettingsId);
          }
        });
      }

      const viewStateRequest = {
        id: view.viewSettingsId,
        title: view.title,
        isDefault: view.isDefault ?? false,
        isPublic: view.isPublic ?? false,
        sharedUserIds: [],
        stageConfigurations,
        stageFilters: configuration?.savedStageFilters || [], // Use saved stage filters from freshly loaded config
        globalFilters: (configuration?.savedGlobalFilters || []).map(filter => ({
          fieldConfigurationId: filter.fieldConfigurationId,
          filter: filter.filter,
          values: typeof filter.values === 'string' ? filter.values : (filter.values ? JSON.stringify(filter.values) : undefined)
        })),
        visibleViewIds: visibleViewIds,
        itemsPerPage: state.itemsPerPage,
        columnWidths: JSON.stringify(state.columnWidths || {}), // Always include column widths
        isUserViewStateUpdate: true
      }
      
      await workflowApi.saveViewState(viewStateRequest)

      // View-switch propagation across the group. The BE's
      // CreateUserViewStateAsync already fans out UserViewState rows to
      // every sibling entity (re-pointing each at its matching sibling
      // ViewSettings + filters), but already-mounted sibling
      // WorkflowProviders won't see those changes until they re-fetch
      // /configurations. Bumping the refresh signal makes them do that
      // immediately — so picking "Default" on VmsRequests also
      // transitions ClientRequest and VmsEmails to Default without the
      // user navigating away and back. origin = this entity so we don't
      // redundantly re-fetch ourselves (our config came back inline
      // above via fetchWorkflowConfiguration).
      if (isRequestsGroupEntity(contextEntityType)) {
        requestsGroupStateRef.current?.bumpRefreshSignal(contextEntityType as RequestsGroupEntityType)
      }
    } catch (error) {
      console.error('Failed to save view state when switching views:', error)
    }
  }, [dispatch, state.stages, state.viewsResponse, workflowApi, contextEntityType])

  const refreshViews = useCallback(async (): Promise<UserViewsResponse | null> => {
    try {
      const viewsResponse = await workflowApi.fetchViews()
      
      // Store the original API response structure
      const allViews: SavedView[] = []
      
      if (viewsResponse.defaultView) {
        allViews.push(viewsResponse.defaultView)
      }
      
      allViews.push(...viewsResponse.userViews)
      allViews.push(...viewsResponse.sharedViews)
      
      dispatch({ type: 'SET_SAVED_VIEWS', payload: allViews })
      dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })
      return viewsResponse
    } catch (error: any) {
      console.error('Failed to refresh views:', error)
      // Keep existing state if refresh fails
      return null
    }
  }, [workflowApi, dispatch])

  // Load configuration and assigned users on mount.
  //
  // When `initialConfiguration` is supplied (the VMS sub-tab path —
  // useVmsConfig has already fetched and resolved it before our provider
  // mounts), seed state via dispatch and skip the GET /configurations
  // request. Without this, every VMS sub-tab open would re-fetch
  // /configurations?entityType=VmsRequest|VmsEmail outside the coalesce
  // window (entries are deleted on settle), doubling the request count.
  // refreshAssignedUsers still fires either way — useVmsConfig doesn't
  // pre-fetch that, and the coalesce helper handles the one-per-entity
  // case fine since both sub-tab providers' calls go out concurrently.
  useEffect(() => {
    if (!isAuthenticated || !token) return
    if (initialConfiguration) {
      dispatch({ type: 'SET_CONFIGURATION', payload: initialConfiguration })
      if (initialConfiguration.itemsPerPage !== undefined) {
        dispatch({ type: 'SET_ITEMS_PER_PAGE', payload: initialConfiguration.itemsPerPage })
      }
    } else {
      refreshConfiguration(undefined, null)
    }
    refreshAssignedUsers()
  }, [isAuthenticated, token, refreshConfiguration, refreshAssignedUsers, initialConfiguration])

  // Add this useEffect after configuration and assignedUsers are loaded.
  // Fetch global facets — deduplicated per-configuration with a key-tracking
  // ref so the request only fires once for a given configuration, but
  // automatically refires when the user switches entity tabs (each tab has
  // its own configuration doc → new id → new fetch). The previous boolean
  // ref permanently latched after the first run, which made a tab switch
  // from WorkOrder → VMS skip the VMS facets fetch entirely.
  const globalFacetsFetchedRef = useRef<string | null>(null)
  useEffect(() => {
    async function fetchGlobalFacetsOnce() {
      // Common preconditions: configuration loaded + at least one globalFilterId
      // to fetch. Without those, the facets request would be a no-op.
      if (
        !state.configuration ||
        !Array.isArray(state.configuration.globalFilterIds) ||
        state.configuration.globalFilterIds.length === 0
      ) {
        return;
      }
      // WorkOrder/Assignment path uses assignedUsers to derive the FBO-context
      // user pool before computing facets — bail until that list has loaded so
      // the resulting facet counts are accurate. VMS doesn't use assignedUsers
      // for facet scoping (its filter is just RequestType eq 'VmsRequest'), so
      // gating on it would make the Filter panel permanently blank for tenants
      // whose AssignedUsers list happens to be empty or slow to load. Skip the
      // gate for VMS.
      if (!isRequestTabEntityType && (!state.assignedUsers || state.assignedUsers.length === 0)) {
        return;
      }
      // Dedupe per entity tab. Fetching again for the same tab is wasteful
      // (facets stable until config changes); fetching for a different tab is
      // required to refresh the filter panel — without per-tab keying, a
      // WorkOrder → VMS switch would silently skip the VMS fetch because the
      // boolean ref was already latched true.
      const configKey = contextEntityType ?? 'default';
      if (globalFacetsFetchedRef.current === configKey) return;
      globalFacetsFetchedRef.current = configKey;

      const facetKeys = getFacetKeysFromConfiguration(state.configuration);
      const selectFields = getSelectFieldsFromConfiguration(state.configuration);
      try {
        const select = selectFields.join(',');
        // For VMS sub-tabs, scope by the DocumentType discriminator derived
        // from the ACTIVE contextEntityType — not a hardcoded value.
        //   • VmsRequest rows share gw-client-request-index with ClientRequest
        //     docs, so the discriminator isolates them.
        //   • VmsEmail rows live in gw-vms-email-index (their own index), and
        //     the discriminator there is 'VmsEmail'.
        // The earlier hardcoded `'VmsRequest'` was a copy-paste from the
        // VMS-only era; when VmsEmail was added as a second sub-tab the
        // filter wasn't parameterised, so the email facet fetch asked for
        // VmsRequest rows against the email index → 0 rows → empty Filter
        // panel on the Emails tab. RequestType on a VMS doc carries the
        // request flavour (Onboarding / Extension / etc.), NOT the literal
        // entity name, so it can't be used as a swap. VMS docs don't have a
        // Stage column, so the WorkOrder-shaped guard isn't usable either.
        let filter = isRequestTabEntityType
          ? `DocumentType eq '${contextEntityType}'`
          : `DocumentType eq 'Assignment' and Stage ne '' and Stage ne null and Stage ne 'Discarded' and IsDeleted eq false`;
        const response = await workflowApi.searchWorkflowItems(
          undefined,
          undefined,
          [],
          1,
          0,
          '*',
          select,
          filter,
          undefined,
          facetKeys
        );
        dispatch({ type: 'SET_FACETS', payload: response.facets });
      } catch (err) {
        console.error('Failed to fetch global facets', err);
      }
    }
    fetchGlobalFacetsOnce();
    // Include contextEntityType so a tab switch (WorkOrder ↔ VMS ↔ VmsEmail)
    // re-evaluates the dedupe key and fires a fresh fetch for the new tab.
    // isRequestTabEntityType is derived from contextEntityType but listed explicitly
    // for the eslint deps lint.
  }, [state.configuration, state.assignedUsers, contextEntityType, isRequestTabEntityType]);

  // Set current view when configuration and savedViews are both loaded
  // Only set if no current view exists (initial load) or if current view doesn't match configuration
  useEffect(() => {
    if (state.configuration && state.configuration.viewSettingsId && state.viewsResponse) {
      // Check if current view matches the configuration's viewSettingsId
      const currentViewMatches = state.currentView?.viewSettingsId === state.configuration.viewSettingsId;
      
      // If current view doesn't match or doesn't exist, find and set the correct view
      if (!currentViewMatches) {
        let matchingView: SavedView | null = null;
        
        // Check default view first
        if (state.viewsResponse.defaultView?.viewSettingsId === state.configuration.viewSettingsId) {
          matchingView = state.viewsResponse.defaultView;
        }
        // Check user views
        if (!matchingView) {
          matchingView = state.viewsResponse.userViews.find(v => v.viewSettingsId === state.configuration!.viewSettingsId) || null;
        }
        // Check shared views
        if (!matchingView) {
          matchingView = state.viewsResponse.sharedViews.find(v => v.viewSettingsId === state.configuration!.viewSettingsId) || null;
        }
        // Check public views
        if (!matchingView) {
          matchingView = state.viewsResponse.publicViews.find(v => v.viewSettingsId === state.configuration!.viewSettingsId) || null;
        }
        // Fallback to savedViews
        if (!matchingView) {
          matchingView = state.savedViews.find(view => view.viewSettingsId === state.configuration!.viewSettingsId) || null;
        }
        
        if (matchingView) {
          dispatch({ type: 'SET_CURRENT_VIEW', payload: matchingView });
        }
      }
    }
  }, [state.configuration, state.savedViews, state.viewsResponse, state.currentView, dispatch]);

  // Computed values
  const currentUser = state.currentUser
  const currentStage = state.currentStage
  const isFilterDrawerOpen = state.isFilterDrawerOpen
  const isBulkEditModalOpen = state.isBulkEditModalOpen
  const isLoadingColumns = state.loadingState === LoadingState.LOADING_COLUMNS
  const savedViews = state.savedViews
  const currentView = state.currentView
  const hasUnsavedChanges = state.hasUnsavedChanges
  const filters = state.filters
  const workOrders = state.workflowItems
  const totalCount = state.totalCount
  const hasNextPage = state.hasNextPage
  const hasPreviousPage = state.hasPreviousPage
  const columns = state.columns
  const visibleColumns = state.visibleColumns
  const selectedItems = state.selectedItems
  const currentPage = state.currentPage
  const pageSize = state.pageSize
  const isLoadingWorkOrders = state.loadingState === LoadingState.LOADING_WORK_ORDERS
  const isLoadingRows = state.loadingState === LoadingState.LOADING_ROWS
  const configuration = state.configuration
  const isLoadingConfiguration = state.isLoadingConfiguration ?? false
  const configurationError = state.configurationError ?? null
  const stages = state.stages
  const assignedUsers = state.assignedUsers
  const isLoadingAssignedUsers = state.isLoadingAssignedUsers ?? false
  const assignedUsersError = state.assignedUsersError ?? null
  const facets = state.facets

  const setStageCounts = useCallback((counts: Record<string, number>) => {
    dispatch({ type: 'SET_STAGE_COUNTS', payload: counts })
  }, [dispatch])

  // Actions
  const setCurrentStage = useCallback((stage: WorkflowStage) => {
    dispatch({ type: 'SET_CURRENT_STAGE', payload: stage })
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('wfh_active_stage', stage)
      }
    } catch {
      // ignore storage errors — persistence is best-effort
    }
    // When stage changes, check if we need to update client-specific custom fields
    // This handles the case where different stages have different stage-level client filters
    if (state.configuration) {
      dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: true })
    }
  }, [dispatch, state.configuration])

  const toggleFilterDrawer = useCallback(() => {
    dispatch({ type: 'TOGGLE_FILTER_DRAWER' })
  }, [dispatch])

  const updateFilter = useCallback((key: string, value: any) => {
    dispatch({ type: 'UPDATE_FILTER', payload: { key, value } })
  }, [dispatch])

  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_FILTERS' })
  }, [dispatch])

  const setSelectedItems = useCallback((items: string[]) => {
    dispatch({ type: 'SET_SELECTED_ITEMS', payload: items })
  }, [dispatch])

  const toggleItemSelection = useCallback((itemId: string) => {
    dispatch({ type: 'TOGGLE_ITEM_SELECTION', payload: itemId })
  }, [dispatch])

  const selectAllItems = useCallback(() => {
    const allItemIds = state.workflowItems.map(item => item.id!).filter(Boolean)
    dispatch({ type: 'SELECT_ALL_ITEMS', payload: allItemIds })
  }, [state.workflowItems, dispatch])

  const deselectAllItems = useCallback(() => {
    dispatch({ type: 'DESELECT_ALL_ITEMS' })
  }, [dispatch])

  const setCurrentPage = useCallback((page: number) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page })
  }, [dispatch])

  const setPageSize = useCallback((size: number) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: size })
  }, [dispatch])

  const updateVisibleColumns = useCallback((columns: FieldConfiguration[]) => {
    dispatch({ type: 'UPDATE_VISIBLE_COLUMNS', payload: columns })
  }, [dispatch])

  const removeWorkOrder = useCallback((orderId: string) => {
    dispatch({ type: 'REMOVE_WORK_ORDER', payload: orderId })
  }, [dispatch])

  const removeWorkOrdersBulk = useCallback((orderIds: string[]) => {
    orderIds.forEach(orderId => {
      dispatch({ type: 'REMOVE_WORK_ORDER', payload: orderId })
    })
  }, [dispatch])

  // Ref to store the latest state for the debounced function
  const stateRef = useRef(state)
  
  // Update the ref whenever state changes
  useEffect(() => {
    stateRef.current = state
  }, [state])

  // Debounced function to save view state when filters change
  const debouncedSaveViewState = useCallback(
    debounce(async () => {
      // Get the current state from the ref
      const currentState = stateRef.current

      // The previous guard bailed out whenever currentView was null. That
      // killed the save for any callsite where the user has interacted
      // before a default view has loaded (common on the VMS tab, where
      // /views returns no default until one has been initialized). The
      // direct saveViewState call paths (column reorder, edit columns)
      // don't have this guard — they send id: undefined and the backend
      // creates/upserts the user's view state. Match that behavior here.
      // Only skip when we don't have a configuration to describe at all.
      if (!currentState.configuration) {
        return;
      }

      try {
        // Build stage configurations from current state. `showCompleted`
        // is included so the toggle survives page reloads and new tabs —
        // without it, debouncedSaveViewState would persist everything else
        // (pin, sort, filters) but silently drop the user's toggle choice,
        // and the next session would read it back as false even after a
        // server round-trip "succeeded".
        const stageConfigurations = currentState.stages.map(stage => ({
          stageConfigurationId: stage.stageId,
          fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
          pinnedColumnId: (currentState.pinnedColumnsByStage || {})[stage.stageId] || null,
          sortColumn: (currentState.sortByStage || {})[stage.stageId]?.columnCode || null,
          sortDirection: (currentState.sortByStage || {})[stage.stageId]?.direction || null,
          showCompleted: (currentState.showCompletedByStage || {})[stage.stageId] ?? false,
        }))

        // Serialize stage filters with values as JSON strings
        const serializedStageFilters = currentState.stageFilters.map(filter => ({
          ...filter,
          values: filter.values ? JSON.stringify(filter.values) : undefined
        }))

        // Get all visible view IDs from the views response
        const visibleViewIds: string[] = [];
        
        // Add default view if it exists and is visible
        if (currentState.viewsResponse?.defaultView && currentState.viewsResponse.defaultView.isVisible !== false) {
          visibleViewIds.push(currentState.viewsResponse.defaultView.viewSettingsId);
        }
        
        // Add user views that are visible
        if (currentState.viewsResponse?.userViews) {
          currentState.viewsResponse.userViews.forEach(view => {
            if (view.isVisible !== false) {
              visibleViewIds.push(view.viewSettingsId);
            }
          });
        }
        
        // Add shared views that are visible
        if (currentState.viewsResponse?.sharedViews) {
          currentState.viewsResponse.sharedViews.forEach(view => {
            if (view.isVisible !== false) {
              visibleViewIds.push(view.viewSettingsId);
            }
          });
        }
        
        // Add public views that are visible
        if (currentState.viewsResponse?.publicViews) {
          currentState.viewsResponse.publicViews.forEach(view => {
            if (view.isVisible !== false) {
              visibleViewIds.push(view.viewSettingsId);
            }
          });
        }

        // Serialize columnWidths before sending to backend
        const serializedColumnWidths = JSON.stringify(currentState.columnWidths)

        const viewStateRequest = {
          // currentView is null on the first save after a fresh tab load
          // (no defaultView returned from /views yet). Backend tolerates a
          // missing id — it upserts under the user/tenant/stage key. Title
          // and visibility flags fall back to safe defaults; the user can
          // promote the resulting saved state to a named view later.
          id: currentState.currentView?.viewSettingsId,
          title: currentState.currentView?.title,
          isDefault: false,
          isPublic: currentState.currentView?.isPublic ?? false,
          sharedUserIds: [],
          stageConfigurations,
          stageFilters: serializedStageFilters,
          globalFilters: currentState.globalFilters.map(filter => ({
            fieldConfigurationId: filter.fieldConfigurationId,
            filter: filter.filter,
            values: typeof filter.values === 'string' ? filter.values : (filter.values ? JSON.stringify(filter.values) : undefined)
          })),
          visibleViewIds: visibleViewIds,
          itemsPerPage: currentState.itemsPerPage,
          columnWidths: serializedColumnWidths,
          isUserViewStateUpdate: true
        }
        
        await workflowApi.saveViewState(viewStateRequest)
      } catch (error) {
        console.error('Failed to save view state:', error)
      }
    }, 1000), // 1 second debounce
    [workflowApi] // Only depend on workflowApi, state is accessed via ref
  )

  // Helper function to build visibleViewIds from viewsResponse
  const buildVisibleViewIds = useCallback((viewsResponse: UserViewsResponse | null | undefined): string[] => {
    if (!viewsResponse) return []
    
    const visibleViewIds: string[] = []
    
    // Add default view if visible (default view is always visible if it exists)
    if (viewsResponse.defaultView && viewsResponse.defaultView.isVisible !== false) {
      visibleViewIds.push(viewsResponse.defaultView.viewSettingsId)
    }
    
    // Add user views where isVisible is true
    if (viewsResponse.userViews) {
      viewsResponse.userViews.forEach(view => {
        if (view.isVisible === true) {
          visibleViewIds.push(view.viewSettingsId)
        }
      })
    }
    
    // Add shared views where isVisible is true
    if (viewsResponse.sharedViews) {
      viewsResponse.sharedViews.forEach(view => {
        if (view.isVisible === true) {
          visibleViewIds.push(view.viewSettingsId)
        }
      })
    }
    
    // Add public views where isVisible is true
    if (viewsResponse.publicViews) {
      viewsResponse.publicViews.forEach(view => {
        if (view.isVisible === true) {
          visibleViewIds.push(view.viewSettingsId)
        }
      })
    }
    
    return visibleViewIds
  }, [])

  // Save view state immediately without affecting loading or data
  const saveViewStateSilently = useCallback(async (
    updatedPinnedColumns?: Record<string, string | null>,
    viewsResponseOverride?: UserViewsResponse | null,
    updatedSortByStage?: Record<string, { columnCode: string; direction: 'asc' | 'desc' } | null>
  ) => {
    const currentState = stateRef.current
    if (!currentState.currentView?.viewSettingsId) {
      return
    }
    try {
      // Use updated values if provided, otherwise use current state
      const pinnedColumns = updatedPinnedColumns || currentState.pinnedColumnsByStage || {}

      const sortByStage = updatedSortByStage || currentState.sortByStage || {}
      const showCompletedByStage = currentState.showCompletedByStage || {}
      // showCompleted is sourced from currentState (no override param)
      // because the silent save only takes overrides for pin/sort — the
      // show-completed toggle has its own setter path and doesn't need a
      // batched override here. Including it in the payload keeps the silent
      // save from clobbering the persisted toggle when another piece of
      // state (pin/sort) triggered the silent save.
      const stageConfigurations = currentState.stages.map(stage => ({
        stageConfigurationId: stage.stageId,
        fieldConfigurationIds: stage.fieldConfigurations.map(f => f.fieldConfigurationId),
        pinnedColumnId: pinnedColumns[stage.stageId] || null,
        sortColumn: sortByStage[stage.stageId]?.columnCode || null,
        sortDirection: sortByStage[stage.stageId]?.direction || null,
        showCompleted: showCompletedByStage[stage.stageId] ?? false,
      }))
      const serializedStageFilters = currentState.stageFilters.map(filter => ({
        ...filter,
        values: filter.values ? JSON.stringify(filter.values) : undefined
      }))
      
      // Use provided viewsResponse if available (fresh from API), otherwise use current state
      const viewsResponseToUse = viewsResponseOverride !== undefined ? viewsResponseOverride : currentState.viewsResponse
      const visibleViewIds = buildVisibleViewIds(viewsResponseToUse)
      const viewStateRequest = {
        id: currentState.currentView.viewSettingsId,
        title: currentState.currentView.title,
        isDefault: false,
        isPublic: currentState.currentView.isPublic ?? false,
        sharedUserIds: [],
        stageConfigurations,
        stageFilters: serializedStageFilters,
        globalFilters: currentState.globalFilters.map(filter => ({
          fieldConfigurationId: filter.fieldConfigurationId,
          filter: filter.filter,
          values: typeof filter.values === 'string' ? filter.values : (filter.values ? JSON.stringify(filter.values) : undefined)
        })),
        visibleViewIds,
        itemsPerPage: currentState.itemsPerPage,
        columnWidths: JSON.stringify(currentState.columnWidths),
        isUserViewStateUpdate: true
      }
      await workflowApi.saveViewState(viewStateRequest)
    } catch (error) {
      console.error('Failed to silently save view state:', error)
    }
  }, [workflowApi, buildVisibleViewIds])

  // New operations
  const setStageFilters = useCallback((filters: { stageId: string; fieldConfigurationId: string; filter: string; values?: any }[]) => {
    // Check if Client filter is being changed in stage filters
    const clientField = state.configuration?.fieldsInUse.find(f => f.columnCode === 'Client')
    if (clientField && state.configuration && state.currentStage) {
      const currentStageConfig = state.stages.find(s => s.stageCode === state.currentStage)
      if (currentStageConfig) {
        const oldClientFilter = state.stageFilters.find(
          f => f.stageId === currentStageConfig.stageId && 
               f.fieldConfigurationId === clientField.fieldConfigurationId
        )
        const newClientFilter = filters.find(
          f => f.stageId === currentStageConfig.stageId && 
               f.fieldConfigurationId === clientField.fieldConfigurationId
        )
        
        // Compare filters to detect change (including existence check)
        const oldFilterStr = oldClientFilter?.filter || ''
        const newFilterStr = newClientFilter?.filter || ''
        
        // Also check if existence changed (filter added or removed)
        const oldExists = !!oldClientFilter
        const newExists = !!newClientFilter
        
        if (oldFilterStr !== newFilterStr || oldExists !== newExists) {
          // Client filter changed - set flag immediately before updating filters
          // Only set flag if we have the necessary context to handle it
          dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: true })
        }
      }
    }
    
    dispatch({ type: 'SET_STAGE_FILTERS', payload: filters })
    // Call view-state endpoint with updated filters
    debouncedSaveViewState()
  }, [dispatch, debouncedSaveViewState, state.configuration, state.currentStage, state.stages, state.stageFilters])

  const setGlobalFilters = useCallback((filters: { fieldConfigurationId: string; filter: string; values?: string }[]) => {
    // Check if Client filter is being changed
    const clientField = state.configuration?.fieldsInUse.find(f => f.columnCode === 'Client')

    if (clientField && state.configuration && state.currentStage) {
      const oldClientFilter = state.globalFilters.find(f => f.fieldConfigurationId === clientField.fieldConfigurationId)
      const newClientFilter = filters.find(f => f.fieldConfigurationId === clientField.fieldConfigurationId)

      // Compare filters to detect change (including existence check)
      const oldFilterStr = oldClientFilter?.filter || ''
      const newFilterStr = newClientFilter?.filter || ''

      // Also check if existence changed (filter added or removed)
      const oldExists = !!oldClientFilter
      const newExists = !!newClientFilter

      if (oldFilterStr !== newFilterStr || oldExists !== newExists) {
        // Client filter changed - set flag immediately before updating filters
        // Only set flag if we have the necessary context to handle it
        dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: true })
      }
    }

    dispatch({ type: 'SET_GLOBAL_FILTERS', payload: filters })
    // Call view-state endpoint with updated filters
    debouncedSaveViewState()
  }, [dispatch, debouncedSaveViewState, state.configuration, state.currentStage, state.globalFilters])

  const clearAllFilters = useCallback(() => {
    // CLEAR_ALL_FILTERS drops the temporary "Showing" filter too, so the
    // cross-tab search stash that would restore it on the next tab remount
    // must die with it — otherwise clearing from any path that doesn't go
    // through the grid's dismissTemporaryFilter (e.g. the FilterDrawer's
    // Clear All) leaves a ghost that resurrects the pill later.
    try {
      sessionStorage.removeItem(`wfh_pending_search_filter.${contextEntityType}`)
    } catch { /* storage unavailable */ }
    dispatch({ type: 'CLEAR_ALL_FILTERS' })
    // Call view-state endpoint with cleared filters
    debouncedSaveViewState()
  }, [dispatch, debouncedSaveViewState, contextEntityType])

  const applyAssignmentSearch = useCallback((stage: string, assignmentId: number, stageFilters: any[]) => {
    dispatch({ 
      type: 'APPLY_ASSIGNMENT_SEARCH', 
      payload: { stage, assignmentId, stageFilters } 
    })
  }, [dispatch])

  const clearTemporaryFilter = useCallback(() => {
    dispatch({ type: 'CLEAR_TEMPORARY_FILTER' })
  }, [dispatch])

  // Used by cross-tab navigation (e.g. VmsEmail → linked VmsRequest) to
  // narrow the grid to a single id without touching saved view state.
  // The reducer clears this when the user changes stage/filters, mirroring
  // the lifecycle of applyAssignmentSearch's temporary filter.
  // `pauseStageFilters` is for scopes that arrive INDEPENDENT of the stage's
  // saved column filters — a `?worker=` deep link. Row-scope handoffs leave it
  // off: on Requests tabs the pause is unconditional anyway, and on WorkOrder
  // the assignment-search flow's stage filters are the scope.
  const applyTemporaryQueryFilter = useCallback((
    filter: string,
    label?: string,
    options?: { pauseStageFilters?: boolean },
  ) => {
    dispatch({
      type: 'APPLY_TEMPORARY_QUERY_FILTER',
      payload: { filter, label, pauseStageFilters: options?.pauseStageFilters },
    })
  }, [dispatch])

  const setItemsPerPage = useCallback((itemsPerPage: number | null) => {
    dispatch({ type: 'SET_ITEMS_PER_PAGE', payload: itemsPerPage })
    // Call view-state endpoint to save the itemsPerPage setting
    debouncedSaveViewState();
    // Remove the manual refreshData call - let the grid's main effect handle it
    // The grid's useEffect will trigger when pageSize changes
  }, [dispatch, debouncedSaveViewState])

  // Function to manually set code values (for when window.PhoenixCodeValues is not available)
  const setCodeValues = useCallback((codeValuesData: any) => {
    if (typeof window !== "undefined") {
      // Set the global code values
      (window as any).PhoenixCodeValues = codeValuesData
      
      // Dispatch a custom event to notify the hook
      window.dispatchEvent(new CustomEvent('phoenixCodeValuesLoaded', { 
        detail: codeValuesData 
      }))
    }
  }, [])

  // Force-fetch code values on initialization. Goes through the module-level
  // deduper in `lib/code-values-loader` so concurrent / nested WorkflowProvider
  // mounts (e.g. the per-sub-tab provider that vms-sub-tab-content spins up
  // when opening the VmsRequest tab) all share a single in-flight network
  // request rather than racing their own. The deduper writes to
  // `window.PhoenixCodeValues` and emits `phoenixCodeValuesLoaded` — we mirror
  // the result into local state afterwards so anything still reading
  // `codeValuesData` off this context keeps working.
  const refreshCodeValues = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING_CODE_VALUES', payload: true })
      await ensureCodeValuesLoaded(token)
      if (typeof window !== "undefined" && window.PhoenixCodeValues) {
        dispatch({ type: 'SET_CODE_VALUES_DATA', payload: window.PhoenixCodeValues })
      } else {
        dispatch({ type: 'SET_CODE_VALUES_ERROR', payload: 'Code values unavailable after load' })
      }
    } catch {
      dispatch({ type: 'SET_CODE_VALUES_ERROR', payload: 'Failed to load code values' })
    }
  }, [token])

  const setShowCompleted = useCallback((stageId: string, showCompleted: boolean) => {
    dispatch({ type: 'SET_SHOW_COMPLETED', payload: { stageId, showCompleted } })
    debouncedSaveViewState()
  }, [debouncedSaveViewState])

  // Force fetch code values on initialization.
  //
  // Guards in order:
  //   1. window.PhoenixCodeValues already set — common case for nested
  //      WorkflowProvider mounts (each VMS sub-tab spins up its own provider
  //      via vms-sub-tab-content). Without this synchronous check, the nested
  //      provider's fresh local state (state.codeValuesData = undefined) would
  //      trip the effect and trigger a duplicate /api/code request before
  //      useCodeValues's 500ms poll caught up.
  //   2. Already mid-load on this provider — local guard against React-strict
  //      double-invocation in dev.
  //
  // If both gates pass, refreshCodeValues delegates to the module-level
  // deduper so even racing nested mounts hit the network exactly once.
  useEffect(() => {
    if (typeof window !== "undefined" && window.PhoenixCodeValues) return;
    if (state.codeValuesData || state.isLoadingCodeValues) return;
    refreshCodeValues();
  }, [state.codeValuesData, state.isLoadingCodeValues, refreshCodeValues]);

  // Column widths operations
  const setColumnWidths = useCallback((columnWidths: Record<string, number>) => {
    dispatch({ type: 'SET_COLUMN_WIDTHS', payload: columnWidths })
    // Call view-state endpoint with updated column widths
    debouncedSaveViewState()
  }, [dispatch, debouncedSaveViewState])

  // Optimistic only — the save itself is done by useMessageNotifications, which owns the minimal
  // payload. Shared state so the modal, the menu label and every row badge move together.
  const setMessageNotificationsEnabled = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_MESSAGE_NOTIFICATIONS', payload: enabled })
  }, [dispatch])

  // Pinned column operation
  const setPinnedColumn = useCallback((columnId: string | null) => {
    const currentStageConfig = stateRef.current.stages.find(s => s.stageCode === stateRef.current.currentStage)
    const stageId = currentStageConfig?.stageId
    if (!stageId) return
    
    // Create updated pinned columns map
    const updatedPinnedColumns = {
      ...(stateRef.current.pinnedColumnsByStage || {}),
      [stageId]: columnId
    }
    
    dispatch({ type: 'SET_PINNED_COLUMN', payload: { stageId, columnId } })
    // No local persistence; configuration/view-state handles saving
    // Persist to view-state immediately with the updated pinned column info
    void saveViewStateSilently(updatedPinnedColumns)
  }, [saveViewStateSilently])

  // Sort state per stage — persisted to view-state alongside pinned columns
  const setSortForStage = useCallback((sort: { columnCode: string; direction: 'asc' | 'desc' } | null) => {
    const currentStageConfig = stateRef.current.stages.find(s => s.stageCode === stateRef.current.currentStage)
    const stageId = currentStageConfig?.stageId
    if (!stageId) return

    // Build the updated sort map before dispatch (state hasn't updated yet)
    const updatedSortByStage = {
      ...(stateRef.current.sortByStage || {}),
      [stageId]: sort
    }

    dispatch({ type: 'SET_SORT_STATE', payload: { stageId, sort } })
    // Persist immediately with the fresh sort map (same pattern as pinned column)
    void saveViewStateSilently(undefined, undefined, updatedSortByStage)
  }, [saveViewStateSilently])

  // Delete view
  const deleteView = useCallback(async (viewId: string) => {
    try {
      await workflowApi.deleteViewSettings(viewId)

      // Refresh views after deletion
      const viewsResponse = await workflowApi.fetchViews()
      dispatch({ type: 'SET_VIEWS_RESPONSE', payload: viewsResponse })

      // If the deleted view was the current view, switch to default view
      if (state.currentView?.viewSettingsId === viewId) {
        const defaultView = viewsResponse.defaultView || null
        dispatch({ type: 'SET_CURRENT_VIEW', payload: defaultView })
        // Refresh configuration with the default view
        if (defaultView) {
          refreshConfiguration(undefined, defaultView.viewSettingsId)
        } else {
          refreshConfiguration(undefined, null)
        }
      }

      // BE deleted siblings + reset their UserViewState GlobalFilters to
      // []. Already-mounted sibling providers need to refresh so their
      // views dropdown drops the deleted view AND their active filters
      // reset along with the deleted view. Passing origin=null so even
      // the originating tab re-fetches if it's still mounted via this
      // provider — the local refreshConfiguration above already covers
      // the active-view branch but other providers in the same hub need
      // a separate trigger.
      requestsGroupStateRef.current?.bumpRefreshSignal(null)
    } catch (error) {
      console.error('Failed to delete view:', error)
      throw error
    }
  }, [state.currentView, workflowApi, dispatch, refreshConfiguration])

  const contextValue: WorkflowContextType = {
    // State
    state,
    currentUser,
    currentStage,
    isFilterDrawerOpen,
    isBulkEditModalOpen,
    isLoadingColumns,
    savedViews,
    currentView,
    hasUnsavedChanges,
    filters,
    
    // View data from API
    viewsResponse: state.viewsResponse, // This will be populated by the useEffect that fetches views
    
    // Data properties for WorkflowGrid
    workOrders,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    columns,
    visibleColumns,
    selectedItems,
    currentPage,
    pageSize,
    
    // Loading states
    isLoadingWorkOrders,
    isLoadingRows,
    
    // Actions
    setCurrentStage,
    toggleFilterDrawer,
    updateFilter,
    clearFilters,
    setSelectedItems,
    toggleItemSelection,
    selectAllItems,
    deselectAllItems,
    setCurrentPage,
    setPageSize,
    setItemsPerPage,
    updateVisibleColumns,
    
    // Data operations
    refreshData,
    updateWorkOrderField,
    updateWorkOrderFieldOptimistically,
    removeWorkOrder,
    removeWorkOrdersBulk,
    bulkUpdateWorkOrderField,
    applyOptimisticUpdates,
    bulkAssignWorkOrders,
    
    // Modal operations
    openBulkEditModal,
    closeBulkEditModal,
    
    // View operations
    setCurrentView,
    setSavedViews,
    setUnsavedChanges,
    saveCurrentView,
    saveViewAs,
    deleteView,
    updateViewMetadata,
    refreshViews,
    
    // Column operations
    addCustomColumn,
    deleteColumn,

    // Configuration operations
    refreshConfiguration,
    updateConfigurationDirectly,
    configuration,
    isLoadingConfiguration,
    configurationError,

    // Stages
    stages,

    // Assigned Users operations
    refreshAssignedUsers,
    assignedUsers,
    isLoadingAssignedUsers,
    assignedUsersError,

    // Facets
    facets,

    // Stage tab counts
    stageCounts: state.stageCounts,
    setStageCounts,

    // New operations
    setStageFilters,
    setGlobalFilters,
    clearAllFilters,
    applyAssignmentSearch,
    applyTemporaryQueryFilter,
    clearTemporaryFilter,

    // New state for stageFilters and globalFilters
    stageFilters: state.stageFilters,
    globalFilters: state.globalFilters,
    temporaryQueryFilter: state.temporaryQueryFilter,
    temporaryQueryFilterLabel: state.temporaryQueryFilterLabel,
    temporaryQueryFilterPausesStageFilters: state.temporaryQueryFilterPausesStageFilters,

    // New state for work orders facets
    workOrdersFacets: state.workOrdersFacets,

    // Code Values
    codeValues,
    setCodeValues,
    
    // Code Values State
    codeValuesData: state.codeValuesData,
    isLoadingCodeValues: state.isLoadingCodeValues ?? false,
    codeValuesError: state.codeValuesError ?? null,
    refreshCodeValues,

    // Column Widths operations
    setColumnWidths,
    setMessageNotificationsEnabled,

    // Pinned column operations
    setPinnedColumn,

    // Sort state per stage
    sortByStage: state.sortByStage || {},
    setSortForStage,

    // Save view state silently
    saveViewStateSilently,

    // Show-completed toggle per stage
    showCompletedByStage: state.showCompletedByStage || {},
    setShowCompleted,
  }

  // Effect to handle client filter changes and fetch custom fields.
  // Holds WHICH client is being fetched and WHICH run owns the slot (null = idle): the client is what
  // dedupes, the request id is what releases. See the note at the fetch site.
  const isFetchingCustomFields = useRef<{ client: string; requestId: number } | null>(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    // Only proceed if flag is set (meaning client filter changed)
    if (!state.isHandlingClientFilterChange) return

    // Configuration and stage should be available since we check before setting flag.
    // If they're missing, clear the flag so the grid's data-fetch effect isn't blocked.
    if (!state.configuration || !state.currentStage) {
      dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: false })
      return
    }

    // VMS entity types have a Client field too, but the per-client custom
    // fields machinery here is WorkOrder/Assignment-specific: it looks the
    // client up by name via a `DocumentType eq 'Assignment'` search, reads
    // FboId off the resulting WorkOrder row, and then fetches that client's
    // custom-field catalog to overlay onto the grid. None of that applies
    // to VmsRequest — the Client value is already structured (FboId is on
    // the doc), there are no FBO-side custom fields to overlay, and the
    // Assignment-shaped lookup query has been confusing reviewers who see
    // it in the network panel fired on a VMS grid. Skip the whole thing.
    //
    // The requestIdRef bump invalidates any in-flight non-VMS custom-fields
    // lookup that started before the user switched to a VMS tab. Without
    // it, a WorkOrder-side lookup kicked off seconds ago could still
    // resolve and dispatch APPLY_CUSTOM_FIELDS into VMS state — the async
    // arms below at lines ~2741/2759 only abort when currentRequestId !==
    // requestIdRef.current, so bumping here forces every stale arm to lose
    // the comparison.
    if (isRequestTabEntityType) {
      requestIdRef.current += 1
      dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: false })
      return
    }

    let shouldRefresh = false
    let shouldFetchCustomFields = false
    let shouldClearCustomFields = false
    let clientName: string | null = null

    // Immediately analyze the situation synchronously
    const clientField = state.configuration?.fieldsInUse.find(f => f.columnCode === 'Client')
    
    if (!clientField) {
      // No Client field, clear flag immediately and let grid handle it
      dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: false })
      return
    }

    // Helper to parse filter values from filter string
    const parseFilterValues = (filter: { filter: string; values?: string | any }): { values: string[]; hasBlank: boolean } => {
      try {
        if (filter.values) {
          // Check if values is already an object or needs parsing
          let metadata: any
          if (typeof filter.values === 'string') {
            metadata = JSON.parse(filter.values)
          } else {
            // Already parsed object
            metadata = filter.values
          }
          
          if (metadata.isSelectAll) return { values: [], hasBlank: false }
          if (metadata.regularValues) {
            return { 
              values: metadata.regularValues, 
              hasBlank: metadata.hasBlanks || false 
            }
          }
        }
      } catch (e) {
        // Fallback to regex parsing
        const matches = Array.from(filter.filter.matchAll(/eq '([^']+)'/g))
        const values = matches.map(m => m[1])
        // Check if filter includes blank condition
        const hasBlank = filter.filter.includes('eq null') || 
                        filter.filter.includes("eq ''") ||
                        filter.filter.includes('eq ""')
        return { values, hasBlank }
      }
      return { values: [], hasBlank: false }
    }

    // Check global filters
    const globalClientFilter = state.globalFilters.find(
      f => f.fieldConfigurationId === clientField.fieldConfigurationId
    )

    // Check stage filters for current stage
    const currentStageConfig = state.stages.find(s => s.stageCode === state.currentStage)
    const stageClientFilter = currentStageConfig
      ? state.stageFilters.find(
          f => f.stageId === currentStageConfig.stageId &&
               f.fieldConfigurationId === clientField.fieldConfigurationId
        )
      : null

    // Parse both filter values
    const globalParsed = globalClientFilter ? parseFilterValues(globalClientFilter) : null
    const stageParsed = stageClientFilter ? parseFilterValues(stageClientFilter) : null

    // Determine the effective single client selection
    let globalClient: string | null = null
    let stageClient: string | null = null

    if (globalParsed && globalParsed.values.length === 1 && globalParsed.values[0] !== '__BLANK__' && !globalParsed.hasBlank) {
      globalClient = globalParsed.values[0]
    }

    if (stageParsed && stageParsed.values.length === 1 && stageParsed.values[0] !== '__BLANK__' && !stageParsed.hasBlank) {
      stageClient = stageParsed.values[0]
    }

    // Decision logic:
    // 1. If both global and stage have different single clients → Clear custom fields and refresh
    // 2. If both global and stage have the SAME single client → Keep existing custom fields (no fetch if already applied)
    // 3. If only global has single client → Use that client
    // 4. If only stage has single client → Use that client
    // 5. If neither has single client or has blanks/multiple → Clear custom fields and refresh

    if (globalClient && stageClient) {
      if (globalClient === stageClient) {
        // Same client in both - keep/fetch for this client
        clientName = globalClient
        // Only fetch if not already applied for this client
        if (state.activeClientName !== clientName) {
          shouldFetchCustomFields = true
        }
        // Otherwise, keep existing custom fields, just refresh data
        else {
          shouldRefresh = true
        }
      } else {
        // Different clients - conflict, clear custom fields and refresh
        shouldClearCustomFields = true
        shouldRefresh = true
      }
    } else if (globalClient) {
      // Only global has single client
      clientName = globalClient
      if (state.activeClientName !== clientName) {
        shouldFetchCustomFields = true
      } else {
        shouldRefresh = true
      }
    } else if (stageClient) {
      // Only stage has single client
      clientName = stageClient
      if (state.activeClientName !== clientName) {
        shouldFetchCustomFields = true
      } else {
        shouldRefresh = true
      }
    } else {
      // No single client (multiple, blank, or none)
      shouldClearCustomFields = true
      shouldRefresh = true
    }

    // Clear custom fields if needed
    if (shouldClearCustomFields && state.customFieldsApplied) {
      dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
    }

    // Now handle the async part
    async function handleClientFilterChange() {
      try {
        // If we cleared custom fields or just need to refresh, let the grid handle it.
        // The grid's data-fetch effect fires when isHandlingClientFilterChange clears (it's in its deps).
        // Calling refreshData here would cause a duplicate call.
        if ((shouldClearCustomFields && shouldRefresh) || (shouldRefresh && !shouldFetchCustomFields)) {
          return
        }

        if (shouldFetchCustomFields && clientName) {
          // Dedupe on the CLIENT, not on "is anything in flight".
          //
          // This used to be `if (isFetchingCustomFields.current) return` — first-wins. The newer
          // run bailed out AFTER bumping requestIdRef, so the in-flight older run then failed its
          // own `currentRequestId !== requestIdRef.current` check further down and neither run
          // applied anything: the column titles silently stayed un-replaced for the rest of the
          // session (the initial check is one-shot via hasCheckedInitialClient). Reaching it needs
          // nothing exotic — SET_CONFIGURATION rebuilds showCompletedByStage, which is in this
          // effect's deps, so a second configuration dispatch inside the fetch window is enough.
          //
          // Now: a request for the SAME client that is already being fetched is a true duplicate
          // and is skipped; a request for a DIFFERENT client proceeds, and requestIdRef keeps
          // latest-wins correctness. Worst case is one extra in-flight call per client change,
          // and cachedClientCustomFields absorbs the repeat.
          if (isFetchingCustomFields.current?.client === clientName) {
            return
          }

          // Increment request ID to track this specific request
          requestIdRef.current += 1
          const currentRequestId = requestIdRef.current

          isFetchingCustomFields.current = { client: clientName, requestId: currentRequestId }

          try {
            const searchFilter = `DocumentType eq 'Assignment' and Client/Name eq '${clientName.replace(/'/g, "''")}' and FboId ne null and IsDeleted eq false`
            const response = await workflowApi.searchWorkflowItems(
              undefined,
              undefined,
              [],
              1,
              1,
              '*',
              'FboId,Client',
              searchFilter,
              undefined,
              []
            )

            if (response.data.length === 0) {
              console.warn('Client not found with FboId')
              if (state.customFieldsApplied) {
                dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
              }
              // Don't call refreshData here - the grid's useEffect will handle it automatically
              return
            }

            const clientData = response.data[0].client || response.data[0].Client
            if (!clientData) {
              console.warn('Client data not found in response')
              if (state.customFieldsApplied) {
                dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
              }
              // Don't call refreshData here - the grid's useEffect will handle it automatically
              return
            }

            const clientFboId = clientData.fboId || clientData.FboId
            if (!clientFboId) {
              console.warn('Client found but no FboId')
              if (state.customFieldsApplied) {
                dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
              }
              // Don't call refreshData here - the grid's useEffect will handle it automatically
              return
            }

            // Check cache
            const cachedFields = state.cachedClientCustomFields.get(clientFboId)
            if (cachedFields) {
              // Only apply if this is still the latest request
              if (currentRequestId !== requestIdRef.current) return

              dispatch({
                type: 'APPLY_CUSTOM_FIELDS',
                payload: { customFields: cachedFields, clientFboId, clientName }
              })
              // Grid's data-fetch effect will fire when isHandlingClientFilterChange clears
              // and configuration changes from APPLY_CUSTOM_FIELDS
              return
            }

            // Fetch custom fields
            const customFieldsResponse = await workflowApi.fetchClientCustomFields(
              clientFboId,
              FboEntityType.WorkOrder
            )

            // Only apply if this is still the latest request (prevent race conditions)
            if (currentRequestId !== requestIdRef.current) return

            if (customFieldsResponse && customFieldsResponse.CustomFields.length > 0) {
              // Client has custom fields - cache and apply them
              dispatch({
                type: 'CACHE_CLIENT_CUSTOM_FIELDS',
                payload: { clientFboId, customFields: customFieldsResponse.CustomFields }
              })
              dispatch({
                type: 'APPLY_CUSTOM_FIELDS',
                payload: { customFields: customFieldsResponse.CustomFields, clientFboId, clientName }
              })
            } else {
              // Client has no custom fields - clear any previously applied ones
              if (state.customFieldsApplied) {
                dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
              }
              // Still cache the empty result to avoid refetching
              dispatch({
                type: 'CACHE_CLIENT_CUSTOM_FIELDS',
                payload: { clientFboId, customFields: [] }
              })
            }

            // Grid's data-fetch effect will fire when isHandlingClientFilterChange clears
            // and picks up any configuration changes from APPLY_CUSTOM_FIELDS/CLEAR_CUSTOM_FIELDS
          } finally {
            // Release on the REQUEST ID, not the client name. Ownership needs an identity: on
            // X -> Y -> X within one window the slot reads "X" again, but it belongs to the third
            // run, and releasing it by name would let the next X fire the duplicate this guard
            // exists to prevent. requestIdRef already mints exactly that identity.
            if (isFetchingCustomFields.current?.requestId === currentRequestId) {
              isFetchingCustomFields.current = null
            }
          }
        }
      } catch (error) {
        console.error('Failed to handle client filter change:', error)
        if (state.customFieldsApplied) {
          dispatch({ type: 'CLEAR_CUSTOM_FIELDS' })
        }
        // Don't call refreshData here - the grid's useEffect will handle it automatically
      } finally {
        // Always clear the flag
        dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: false })
      }
    }

    handleClientFilterChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isHandlingClientFilterChange, state.showCompletedByStage])

  // Effect to check for client-specific custom fields on initial load
  const hasCheckedInitialClient = useRef(false)
  
  useEffect(() => {
    // Only run once after configuration and filters are loaded
    if (hasCheckedInitialClient.current) return
    if (!state.configuration || !state.currentStage) return
    
    // Wait a tick to ensure filters are loaded from view configuration
    const timeoutId = setTimeout(() => {
      if (hasCheckedInitialClient.current) return
      hasCheckedInitialClient.current = true
      
      // Trigger the client filter change check
      dispatch({ type: 'SET_HANDLING_CLIENT_FILTER_CHANGE', payload: true })
    }, 100)
    
    return () => clearTimeout(timeoutId)
  }, [state.configuration, state.currentStage, dispatch])

  // Publish to the cross-tab Requests group store whenever this tab's dirty
  // state or active view changes. The store lives at the workflow-hub level
  // (RequestsGroupStateProvider), so it survives this tab's own mount /
  // unmount cycles — when the user switches away and back to e.g. VMS, the
  // ClientRequest snapshot persists in the parent store and the save modal
  // can surface "VMS has unsaved changes" without needing the VMS tab to
  // currently be mounted.
  //
  // Skipped for non-group entities (WorkOrder, etc.) and when no provider
  // is mounted above us (useRequestsGroupState returns null) — the hook
  // is a graceful no-op there so legacy tabs don't need to be wrapped.
  //
  // IMPORTANT: pull `publishTab` out via destructure rather than depending
  // on the whole `requestsGroupState` object. publishTab is wrapped in
  // useCallback([]) so it has a stable identity across renders; the parent
  // store's memoized `value` object, by contrast, gets a new reference
  // whenever `tabs` updates — including the update this effect just
  // dispatched. Depending on the whole value would cycle: publish → store
  // update → new value reference → effect re-fires → publish again → loop.
  // Already burned on this once; rather not again.
  const requestsGroupState = useRequestsGroupState()
  // Mirror into the ref declared earlier in the component so the save /
  // delete callbacks can call bumpRefreshSignal without taking a closure
  // dep on the whole context value.
  requestsGroupStateRef.current = requestsGroupState
  const publishTab = requestsGroupState?.publishTab
  useEffect(() => {
    if (suppressRequestsGroupPublish) return
    if (!publishTab) return
    if (!isRequestsGroupEntity(contextEntityType)) return
    const entityType = contextEntityType as RequestsGroupEntityType

    // Build the snapshot the save modal needs to construct a saveViewGroup
    // payload for this tab. Stage configurations are reconstructed from
    // state.stages + per-stage maps (same shape saveCurrentView assembles
    // before POSTing /view-settings — see ~line 1474).
    const stageConfigurations = (state.stages || []).map((stage) => ({
      stageConfigurationId: stage.stageId,
      fieldConfigurationIds: stage.fieldConfigurations.map((f) => f.fieldConfigurationId),
      pinnedColumnId: (state.pinnedColumnsByStage || {})[stage.stageId] || null,
      showCompleted: (state.showCompletedByStage || {})[stage.stageId] ?? false,
      sortColumn: (state.sortByStage || {})[stage.stageId]?.columnCode || null,
      sortDirection: (state.sortByStage || {})[stage.stageId]?.direction || null,
    }))

    // Resolve permission-relevant metadata for the save-modal cross-tab
    // canModify check. "Owner" = the loaded view appears in this tab's own
    // userViews list (the user created it). When viewsResponse hasn't
    // arrived yet, owner falls back to false; the modal then defers to
    // the isDefault / isPublic rules which still gate correctly.
    const currentViewId = state.currentView?.viewSettingsId ?? null
    const userViewsList = state.viewsResponse?.userViews ?? []
    const isOwner = !!currentViewId && userViewsList.some(v => v.viewSettingsId === currentViewId)

    publishTab(entityType, {
      currentViewSettingsId: currentViewId,
      currentViewGroupCode: state.currentView?.viewGroupCode ?? null,
      currentViewTitle: state.currentView?.title ?? null,
      currentViewIsDefault: state.currentView?.isDefault === true,
      currentViewIsPublic: state.currentView?.isPublic === true,
      currentViewIsOwner: isOwner,
      hasUnsavedChanges: state.hasUnsavedChanges,
      stageConfigurations,
      stageFilters: state.stageFilters || [],
      globalFilters: state.globalFilters || [],
    })
  }, [
    suppressRequestsGroupPublish,
    publishTab,
    contextEntityType,
    state.hasUnsavedChanges,
    state.currentView,
    state.viewsResponse,
    state.stages,
    state.stageFilters,
    state.globalFilters,
    state.pinnedColumnsByStage,
    state.showCompletedByStage,
    state.sortByStage,
  ])

  // Cross-tab refresh subscription. When any sibling provider bumps the
  // shared refresh signal (e.g. a save on VMS Emails affects VMS Requests
  // siblings via BE fan-out), this provider re-fetches its configuration
  // so the new filters / view state actually surface without forcing the
  // user to navigate away and back. Skip-self: the originating tab already
  // has the fresh data inline from its own response, so re-fetching there
  // would be a redundant round-trip.
  //
  // Pull `refreshSignal` out via destructure for the same identity reason
  // as publishTab above — depending on `requestsGroupState` directly would
  // re-fire the effect on every snapshot publish.
  const refreshSignal = requestsGroupState?.refreshSignal
  const refreshConfigRef = useRef(refreshConfiguration)
  refreshConfigRef.current = refreshConfiguration
  const lastRefreshNonceRef = useRef<number>(refreshSignal?.nonce ?? 0)
  useEffect(() => {
    if (suppressRequestsGroupPublish) return
    if (!refreshSignal) return
    if (!isRequestsGroupEntity(contextEntityType)) return
    if (refreshSignal.nonce === lastRefreshNonceRef.current) return
    lastRefreshNonceRef.current = refreshSignal.nonce
    // Skip-self: the tab that triggered the bump already has the fresh
    // server response inline. Only siblings need to re-fetch.
    if (refreshSignal.origin === contextEntityType) return
    // Force-refresh, with current viewId so the BE returns the same view
    // we're already on (filters / stage configs from the just-saved doc).
    // skipFilterUpdate is intentionally NOT set — we DO want the
    // SET_CONFIGURATION reducer to refresh state.globalFilters with the
    // BE's new values. preferUserState=true because this is a refresh of
    // the view the user is already on, not an explicit view pick: the
    // user's transient state (UserViewState — their sort/pins/column
    // filters) must survive, matching WorkOrder behavior. Without it, a
    // viewId-specified fetch makes ViewSettings authoritative and this
    // tab resets to the view configuration whenever a sibling tab saves.
    const currentViewId = state.currentView?.viewSettingsId ?? null
    refreshConfigRef.current(true, currentViewId, undefined, true)
  }, [suppressRequestsGroupPublish, refreshSignal, contextEntityType, state.currentView?.viewSettingsId])

  return (
    <WorkflowContext.Provider value={contextValue}>
      {children}
    </WorkflowContext.Provider>
  )
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined)

export function useWorkflow() {
  const context = useContext(WorkflowContext)
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider')
  }
  return context
} 
